import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  analyzeRqtPack,
  hashBytes,
  LatestJobScheduler,
  newInstalledRqtPackId,
  parseTurn,
  renderRoqeetText,
  runRoqeet,
  runRoqeetChangedRange,
  type InstalledRqtPack,
  type RoqeetChangedRange,
  type RqtPackAnalysis,
} from "@roqeet/core";
import {
  BookOpen,
  Bot,
  Braces,
  CheckCircle2,
  Code2,
  Copy,
  Database,
  Eye,
  FileText,
  Gamepad2,
  Image,
  MessageSquare,
  Package,
  Play,
  ShieldAlert,
  SquareMousePointer,
  Upload,
  Wand2,
} from "lucide-react";
import { storage } from "../../state/storage.js";
import { useLive } from "../../state/use-live.js";
import { importGameCard } from "../../state/game-cards.js";
import { useUiStore } from "../../state/ui-store.js";
import {
  RQT_EXAMPLE_CARDS,
  RQT_IMAGE_PLACEHOLDER_NOTE,
  RQT_ROADMAP_CARDS,
  type RqtExampleCard,
} from "../rqt-example-library.js";

type GuideMode = "rqt" | "json";
type GuideRow = {
  title: string;
  ko: string;
  en: string;
  description: string;
  sample: string;
};
type LifecycleId = "input" | "before" | "model" | "after" | "screen";
type SpecStatus = "Stable" | "Preview" | "Experimental" | "Removed";
type SpecGroup = {
  title: string;
  status: SpecStatus;
  body: string;
  examples: string[];
};
type BeginnerCard = {
  title: string;
  body: string;
  example: string;
};
type MinuteStep = {
  label: string;
  title: string;
  body: string;
};
type ConfusionRow = {
  title: string;
  wrong: string;
  right: string;
};
type ExpressionRow = {
  label: string;
  value: string;
  note: string;
};
type GuideAnchor = {
  id: string;
  label: string;
};
type ProblemRow = {
  oldWay: string;
  risk: string;
  roqeetWay: string;
};
type ConceptCard = {
  title: string;
  body: string;
  example: string;
};
type RecipeCard = {
  title: string;
  body: string;
  example: string;
};
type AdvancedCard = {
  title: string;
  body: string;
  examples: string[];
};
type SaveFlowRow = {
  title: string;
  userAction: string;
  storedAs: string;
  note: string;
};
type DiagnosticRow = {
  issue: string;
  message: string;
  fix: string;
};
type LanguageModeRow = {
  mode: string;
  useWhen: string;
  example: string;
  note: string;
};
type GlossaryRow = {
  term: string;
  meaning: string;
  example: string;
};
type CommandIndexRow = {
  command: string;
  role: string;
  useWhen: string;
};

const RUN_CONTEXT = {
  char: "하린",
  user: "민준",
  random: () => 0,
  now: () => Date.UTC(2026, 0, 2, 3, 4),
};

const CHATBOT_SAMPLE = `#!roqeet/rqt 0.2

@설정 호감도 = 55

@프롬프트 "캐릭터" {
  이름: 하린
  말투: 다정하지만 장난스럽게
  사용자: @유저
}

@만약 호감도 >= 50 {
  @캐릭터@은는 @유저에게 편하게 말한다.
}

@버튼 "호감도 올리기" -> affection_up`;

const GAME_SAMPLE = `#!roqeet/rqt 0.2

@설정 hp = 30
@설정 열쇠 = 1

어두운 복도 끝에서 문이 흔들린다.

@만약 hp <= 0 {
  쓰러졌다.
}
@아니면 {
  현재 HP: @hp
}

@랜덤 {
  바닥에서 낡은 쪽지를 발견했다.
  멀리서 발소리가 들린다.
}

@이미지 "corridor.png"
@버튼 "문 열기" -> open_door`;

const GAME_TURN_SAMPLE = `동굴에 들어섰다.
@감소 hp 10
@증가 clues 1
@추가 inventory "녹슨 열쇠"
@장면 bg=cave mood=긴장
@주사위 d20
@선택지 {
  횃불을 켠다
  되돌아간다
}`;

const ASSETBOT_STARTER_SAMPLE = `#!roqeet/rqt 0.2

@설정 호감도 = 35
@설정 표정 = "neutral"
@설정 배경 = "room"

@프롬프트 "캐릭터 카드" {
  이름: 하린
  말투: 조용하지만 장난스럽게
  규칙: 본문은 자연스럽게 쓰고, 상태 변화는 RoQeet 명령으로만 제안한다.
  현재 호감도: @호감도
}

@만약 호감도 >= 60 {
  @설정 표정 = "smile"
  @캐릭터@은는 @유저에게 조금 더 가까이 앉는다.
}
@아니면 {
  @캐릭터@은는 아직 거리를 둔다.
}

@장면 "chat" 배경="room.png" 표정="@표정" {
  @대사 "@캐릭터" {
    @유저, 오늘은 무슨 이야기를 할까?
  }
}

@선택지 {
  - 차분하게 인사한다 -> greet_soft
  - 장난스럽게 칭찬한다 -> compliment
}`;

const FIVE_MINUTE_RQT_SAMPLE = `#!roqeet/rqt 0.2

@game title="Yuna Classroom" description="Tiny relationship scene" icon="sparkles"
@state 유나.호감도 number initial=45 min=0 max=100 mutableBy=ai label="Affection"
@state 유나.표정 enum initial="neutral" values="neutral,smile" mutableBy=ai

@설정 유나.호감도 = 45
@설정 유나.표정 = "neutral"

@프롬프트 "캐릭터" {
  이름: 유나
  말투: 다정하지만 장난스럽게
  현재 호감도: @(유나.호감도)
}

@장면 "교실" 배경="classroom.png" {
  @대사 "유나" {
    오늘도 왔네?
  }
}

@만약 유나.호감도 >= 40 {
  @설정 유나.호감도 = 유나.호감도 + 5
  @설정 유나.표정 = "smile"
  @이미지 "yuna.smile.png"
  @대사 "유나" {
    기분 좋아 보이네.
  }
}

@선택지 {
  - 인사한다 -> greet
  - 무시한다 -> ignore
}`;

const GUIDE_ANCHORS: GuideAnchor[] = [
  { id: "quick-map", label: "한눈에 보기" },
  { id: "problems", label: "왜 필요한가" },
  { id: "five-minute", label: "5분 예제" },
  { id: "first-assetbot", label: "첫 에셋봇" },
  { id: "examples", label: "Examples" },
  { id: "terminology", label: "용어집" },
  { id: "concepts", label: "핵심 개념" },
  { id: "save-validation", label: "저장·검증" },
  { id: "syntax-reference", label: "문법 레퍼런스" },
  { id: "command-index", label: "명령 색인" },
  { id: "recipes", label: "레시피" },
  { id: "package-authoring", label: "패키징" },
  { id: "language-modes", label: "언어 모드" },
  { id: "advanced", label: "고급" },
  { id: "syntax-lab", label: "문법 실험" },
];

const PROBLEM_ROWS: ProblemRow[] = [
  {
    oldWay: "정규식으로 [aff:+5] 같은 상태 태그 파싱",
    risk: "모델이 철자를 틀리면 상태가 누락되거나 중복 적용됩니다.",
    roqeetWay: "state / @설정 / @증가 / @감소를 런타임이 계산",
  },
  {
    oldWay: "HTML 주석에 세이브 데이터 숨김",
    risk: "대화 로그 수정, 요약, 리롤 과정에서 세이브가 깨집니다.",
    roqeetWay: "Save Slot과 .rqg 상태 스냅샷으로 분리",
  },
  {
    oldWay: "<img=\"...\"> 태그 출력을 AI에게 강제",
    risk: "이미지 키가 틀리거나 본문에 섞이면 렌더링이 불안정합니다.",
    roqeetWay: "Asset Registry / @이미지 / show로 등록 에셋만 표시",
  },
  {
    oldWay: "LLM에게 선택지 태그를 끝마다 출력하도록 지시",
    risk: "선택지 개수, 라벨, action명이 매 턴 흔들립니다.",
    roqeetWay: "choice / @선택지 / @버튼을 네이티브 UI로 렌더링",
  },
  {
    oldWay: "Lua나 JS 조각으로 호감도·HP 계산",
    risk: "초보자에게 어렵고 권한/샌드박스 검증이 복잡해집니다.",
    roqeetWay: "화이트리스트 연산식과 Runtime 조건 판정",
  },
  {
    oldWay: "정규식 치환으로 UI와 로그를 억지 구성",
    risk: "모바일 UI, 접근성, 저장/복원이 플랫폼마다 달라집니다.",
    roqeetWay: "Native UI Binding, HUD, 로그, 패키지 메타데이터",
  },
];

const CONCEPT_CARDS: ConceptCard[] = [
  {
    title: "State",
    body: "호감도, HP, 위치처럼 엔진이 기억하고 검증할 값을 선언합니다.",
    example: `@설정 affection = 45
@증가 affection 5
@(affection)`,
  },
  {
    title: "Choice",
    body: "플레이어가 누를 선택지와 action 이름을 본문에서 분리합니다.",
    example: `@선택지 {
  - 인사한다 -> greet
  - 돌아선다 -> leave
}`,
  },
  {
    title: "Scene",
    body: "배경, 분위기, 표정 같은 장면 정보를 출력과 함께 묶습니다.",
    example: `@장면 "교실" 배경="classroom.png" {
  조용한 오후.
}`,
  },
  {
    title: "Asset",
    body: "이미지와 스프라이트는 태그 문자열이 아니라 등록된 에셋 키로 표시합니다.",
    example: `@이미지 "yuna.smile.png"
@설정 expression = "smile"`,
  },
  {
    title: "Prompt",
    body: "LLM에게만 전달할 말투, 세계관, 현재 상태를 별도 청크로 조립합니다.",
    example: `@프롬프트 "캐릭터" {
  말투: 다정하게
  호감도: @(affection)
}`,
  },
  {
    title: "Runtime",
    body: "상태 계산, 조건 분기, 선택지 실행, 미리보기 렌더링은 엔진이 담당합니다.",
    example: `@만약 hp <= 0 {
  쓰러졌다.
}`,
  },
  {
    title: "Save",
    body: "저장해야 할 상태는 대화 텍스트가 아니라 구조화된 프로젝트 상태로 남깁니다.",
    example: `.rqt
  -> .rqg
  -> snapshot`,
  },
];

const RECIPE_CARDS: RecipeCard[] = [
  {
    title: "호감도 시스템",
    body: "상태값을 만들고 선택지 action에서 증가/감소시킵니다.",
    example: `@설정 affection = 45
@증가 affection 5 최대 100
@만약 affection >= 80 { 특별 대사 }`,
  },
  {
    title: "인벤토리",
    body: "획득 목록은 배열 상태로 두고 AI가 제안한 추가 명령만 검증합니다.",
    example: `@추가 inventory "녹슨 열쇠"
@만약 has_key == 참 {
  문을 열 수 있다.
}`,
  },
  {
    title: "HP와 상태이상",
    body: "HP는 범위를 고정하고 독, 출혈 같은 상태는 별도 키로 둡니다.",
    example: `@감소 hp 10 최소 0
@설정 status = "poisoned"
@만약 hp <= 0 { 전투 종료 }`,
  },
  {
    title: "장면 이동",
    body: "location과 background를 분리해 저장하고 화면은 background 에셋을 봅니다.",
    example: `@설정 location = "숲 입구"
@설정 background = "forest_gate"
@이미지 "forest_gate.png"`,
  },
  {
    title: "에셋 조건 표시",
    body: "표정/상태 키에 따라 표시할 이미지를 바꿉니다.",
    example: `@만약 affection >= 60 {
  @설정 expression = "smile"
  @이미지 "yuna.smile.png"
}`,
  },
  {
    title: "숨김 태그 파싱 대체하기",
    body: "숨김 태그와 정규식을 action 노드와 엔진 상태 변경으로 옮깁니다.",
    example: `[aff:+5] -> @증가 affection 5
<img=happy> -> @이미지 "happy.png"
<choice> -> @선택지 { ... }`,
  },
];

const ADVANCED_CARDS: AdvancedCard[] = [
  {
    title: ".rqg",
    body: "사람이 쓴 .rqt를 에디터와 엔진이 검증하기 좋게 정규화한 문서형입니다.",
    examples: ['kind: "roqeet.game"', 'rqtVersion: "0.1"', "nodes: [...]"],
  },
  {
    title: ".rqp",
    body: "스크립트, 에셋, 권한, 메타데이터를 묶어 설치/공유하는 패키지 포맷입니다.",
    examples: ["manifest.json", "game.rqg", "source/main.rqt", "assets/..."],
  },
  {
    title: "권한",
    body: "네트워크, 저장소, 모델 호출, 민감 콘텐츠를 패키지 설치 전에 명시합니다.",
    examples: ["network.fetch", "storage.session", "model.chat"],
  },
  {
    title: "응답 훅",
    body: "모델 호출 전후에 기억 조회, 요약 저장, 런타임 검증을 연결하는 고급 흐름입니다.",
    examples: ["@응답전 { ... }", "@응답후 { ... }", "@트리거 조건 { ... }"],
  },
  {
    title: "마이그레이션",
    body: "GameDefinition JSON, 숨김 태그, 구형 블록 문법을 .rqg 문서형으로 옮깁니다.",
    examples: ["roqeet-game_v1 -> roqeet-game_v2", "@끝 -> { }", "regex tag -> state op"],
  },
];

const BASIC_GUIDE_CARDS: BeginnerCard[] = [
  {
    title: "1. .rqt는 사람이 쓰는 설계도",
    body: "본문은 그대로 화면에 나오고, @로 시작하는 줄은 엔진이 읽는 명령입니다. 처음에는 글 사이에 필요한 명령을 한 줄씩 붙인다고 생각하면 됩니다.",
    example: "문 앞에 섰다.\n@버튼 \"문 열기\" -> open_door",
  },
  {
    title: "2. 상태는 엔진이 가진 기억",
    body: "호감도, HP, 배경, 표정처럼 다음 턴에도 남아야 하는 값은 @설정으로 만들고 @증가, @감소로 바꿉니다.",
    example: "@설정 호감도 = 30\n@증가 호감도 5\n현재 호감도: @호감도",
  },
  {
    title: "3. AI는 말하고, 엔진은 검증한다",
    body: "AI에게 규칙 집행을 맡기지 않습니다. AI는 서술과 제안된 action을 내고, 엔진은 허용된 상태 키와 조건만 반영합니다.",
    example: "본문: 숲이 깊어진다.\n명령: @증가 progress 1",
  },
  {
    title: "4. .rqg는 런타임 게임 문서",
    body: "초보자는 .rqt부터 작성하면 됩니다. .rqg는 에디터, 검증기, 런타임이 읽는 정규화된 게임 문서이며 보안 경계는 아닙니다.",
    example: ".rqt -> compile/lint/lower -> .rqg -> preview",
  },
];

const ASSETBOT_STEPS: MinuteStep[] = [
  {
    label: "1분",
    title: "캐릭터와 핵심 상태를 정한다",
    body: "이름, 말투, 호감도, 표정, 배경처럼 화면과 대화에 계속 쓰일 값만 먼저 만듭니다.",
  },
  {
    label: "2분",
    title: "표정/배경 에셋 키를 만든다",
    body: "표정은 neutral, smile처럼 짧은 키로 두고, 배경은 room, street처럼 이미지와 연결할 이름으로 둡니다.",
  },
  {
    label: "3분",
    title: "AI 서술 가이드를 분리한다",
    body: "@프롬프트에는 말투와 분위기를 넣고, 상태 변경 규칙은 @설정, @만약, @선택지 같은 노드로 둡니다.",
  },
  {
    label: "4분",
    title: "선택지와 상태 변화를 테스트한다",
    body: "왼쪽 편집기에 샘플을 넣고 오른쪽 미리보기에서 출력, 프롬프트, UI, 상태가 따로 잡히는지 확인합니다.",
  },
  {
    label: "5분",
    title: "에셋을 붙이고 내보낸다",
    body: "갤러리에 이미지를 넣은 뒤 표정/배경 키와 연결합니다. 공유할 때는 .rqp으로 묶어 권한과 에셋을 함께 확인합니다.",
  },
];

const COMMON_CONFUSIONS: ConfusionRow[] = [
  {
    title: "@설정과 @증가",
    wrong: "@증가는 숫자를 더하거나 빼는 변화에만 씁니다.",
    right: "처음 값을 만들거나 문자열/참거짓을 바꿀 때는 @설정을 씁니다.",
  },
  {
    title: "본문과 @프롬프트",
    wrong: "본문에 쓴 글은 사용자 화면에 보입니다.",
    right: "모델에게만 줄 지시문은 @프롬프트 블록에 넣습니다.",
  },
  {
    title: "게임 규칙과 AI 지시문",
    wrong: "AI 지시문만으로 HP, 엔딩, 권한을 믿으면 안 됩니다.",
    right: "엔딩 조건, 허용 상태 키, 클램프, 선택지는 엔진 노드와 JSON으로 선언합니다.",
  },
  {
    title: ".rqt와 .rqg",
    wrong: ".rqg을 손으로 먼저 외울 필요는 없습니다.",
    right: ".rqt를 쓰면 에디터가 .rqg 구조를 보여주고 검증합니다.",
  },
];

const SAVE_FLOW_ROWS: SaveFlowRow[] = [
  {
    title: "게임 편집 저장",
    userAction: "게임 편집기에서 저장",
    storedAs: "GameCard + 공식 .rqg 게임 문서",
    note: "저장된 활성 게임은 다시 컴파일되어 새 대화의 게임 목록에 바로 노출됩니다.",
  },
  {
    title: "단일 게임 내보내기",
    userAction: "게임 카드의 내보내기",
    storedAs: ".rqg 공유 파일",
    note: "휘발성 메타는 빼고 공유 가능한 정의만 담습니다. 가져오면 새 id로 저장됩니다.",
  },
  {
    title: "단일 게임 가져오기",
    userAction: "게임 화면의 가져오기",
    storedAs: "검증된 GameCard",
    note: "공식 .rqg 게임 문서와 내부 호환 입력을 검증한 뒤 저장·등록합니다.",
  },
  {
    title: "패키지 설치",
    userAction: ".rqp 설치 미리보기",
    storedAs: "설치된 패키지 레코드",
    note: "권한과 추천 설정을 확인한 뒤 로컬 레지스트리에 등록합니다.",
  },
];

const DIAGNOSTIC_ROWS: DiagnosticRow[] = [
  {
    issue: "정의되지 않은 state 참조",
    message: "endWhenUnknownKey: endingScore를 찾을 수 없음",
    fix: "initialState, stateSchema, mutableKeys, rule 산출 키 중 하나에 같은 키를 선언합니다.",
  },
  {
    issue: "범위가 뒤집힌 clamp",
    message: "clampInvalidBounds: hp의 min이 max보다 큼",
    fix: "min/max 순서를 고치고 기본값이 범위 밖이면 default도 함께 수정합니다.",
  },
  {
    issue: "파생 상태를 LLM이 직접 수정",
    message: "derivedStateMutable: mood가 LLM 수정 가능 상태로 열림",
    fix: "mood 같은 파생 상태는 rule이 계산하게 두고 mutableKeys에서는 제외합니다.",
  },
  {
    issue: "존재하지 않는 asset 참조",
    message: "brokenAssetReference: yuna.happy.png 없음",
    fix: "asset binding과 실제 파일명을 맞추고, 패키지에 assets/ 항목이 포함됐는지 확인합니다.",
  },
  {
    issue: "패키지 manifest 오류",
    message: "RPK020: manifest.json entry.game target is missing",
    fix: "entry.game를 game.rqg처럼 패키지 안에 실제 존재하는 실행 문서 경로로 지정합니다.",
  },
];

const LANGUAGE_MODE_ROWS: LanguageModeRow[] = [
  {
    mode: "초보 기본",
    useWhen: "한국어로 바로 만들고 미리보기할 때",
    example: "@설정 hp = 30\n@증가 hp 5\n@선택지 { ... }",
    note: "현재 안정적으로 실행 가능한 RoQeet 0.2 기본 표면입니다.",
  },
  {
    mode: "English Expert Mode",
    useWhen: "팀 문서, 해외 공유, 코드 리뷰에서 영문 키워드가 편할 때",
    example: "@set hp = 30\n@increase hp 5\n@choices { ... }",
    note: "한글 명령과 같은 런타임 노드로 내려가지만 한 파일 안에서는 한 스타일을 우선하세요.",
  },
  {
    mode: "LLM action output",
    useWhen: "AI 응답 끝에 검증 가능한 상태 변경 후보를 붙일 때",
    example: "@증가 progress 1\n@설정 background = \"pond\"",
    note: "AI는 제안만 하고, 엔진은 허용 키와 범위, 규칙을 검증한 뒤 반영합니다.",
  },
];

const GLOSSARY_ROWS: GlossaryRow[] = [
  {
    term: "RoQeet",
    meaning: "AI 채팅에 상태, 선택지, 에셋, UI, 세이브를 붙이는 창작 런타임과 문법입니다.",
    example: ".rqt -> .rqg -> preview",
  },
  {
    term: ".rqt",
    meaning: "사람이 직접 쓰는 문서입니다. 본문과 @명령을 함께 적습니다.",
    example: '@설정 호감도 = 45',
  },
  {
    term: ".rqg",
    meaning: "에디터, 검증기, 가져오기, 런타임이 읽는 구조화된 결과입니다.",
    example: '{ "kind": "roqeet.game", "nodes": [...] }',
  },
  {
    term: "LLM",
    meaning: "대규모 언어 모델입니다. RoQeet에서는 대사, 묘사, 분위기, action 제안을 담당합니다.",
    example: "캐릭터 반응과 장면 묘사",
  },
  {
    term: "Runtime",
    meaning: "상태 계산, 조건 판정, 선택지 실행, 에셋 표시, 세이브/로드를 보장하는 엔진 부분입니다.",
    example: "호감도 +5, HP clamp, 엔딩 조건",
  },
  {
    term: "IR",
    meaning: "중간 표현입니다. 사람이 쓴 문법을 엔진이 처리하기 쉬운 구조로 낮춘 형태입니다.",
    example: "parse -> lower -> preview",
  },
];

const COMMAND_INDEX_ROWS: CommandIndexRow[] = [
  { command: "@설정", role: "상태 만들기/대입", useWhen: "처음 값, 문자열, 참/거짓, 계산 결과를 저장할 때" },
  { command: "@증가 / @감소", role: "숫자 변경", useWhen: "호감도, HP, 진행도처럼 범위가 있는 숫자를 바꿀 때" },
  { command: "@만약 / @아니면", role: "조건 분기", useWhen: "상태에 따라 대사, 장면, 엔딩을 다르게 보여줄 때" },
  { command: "@프롬프트", role: "LLM 입력", useWhen: "사용자에게 보이지 않는 말투, 세계관, 현재 상태를 모델에 줄 때" },
  { command: "@대사", role: "화자 있는 출력", useWhen: "누가 말했는지 엔진 출력으로 안정 기록할 때" },
  { command: "@선택지 / @버튼", role: "플레이어 입력", useWhen: "AI 텍스트 태그가 아니라 네이티브 선택 UI를 만들 때" },
  { command: "@이미지 / @장면", role: "에셋과 화면", useWhen: "등록된 이미지, 배경, 분위기 키를 화면에 연결할 때" },
  { command: "@주사위", role: "시드 판정", useWhen: "전투, 탐색, 위험 판정처럼 재현 가능한 랜덤 결과가 필요할 때" },
];

const PACKAGE_TREE_SAMPLE = `my-assetbot.rqp
|-- manifest.json
|-- game.rqg
|-- source/main.rqt
|-- assets/yuna/neutral.png
|-- assets/yuna/smile.png
+-- assets/backgrounds/room.png`;

const PACKAGE_MANIFEST_SAMPLE = `{
  "id": "ian.yuna_assetbot",
  "name": "Yuna Assetbot",
  "version": "1.0.0",
  "packVersion": "0.2",
  "languageVersion": "0.2",
  "type": "game",
  "description": "호감도와 표정 에셋을 가진 첫 RoQeet 에셋봇",
  "entry": {
    "game": "game.rqg",
    "script": "source/main.rqt"
  },
  "permissions": {
    "model.chat": "ask",
    "storage.session": true
  },
  "recommendations": {
    "preview.mockAi": true,
    "layout": "assetbot"
  },
  "contentRating": {
    "age": "all",
    "flags": [],
    "requiresAgeGate": false
  }
}`;

const EXPRESSION_ROWS: ExpressionRow[] = [
  { label: "더하기", value: "호감도 + 5", note: "+는 숫자 덧셈입니다. 문자열을 이어 붙일 때는 &를 씁니다." },
  { label: "문자열 연결", value: "name & \"님\"", note: "&는 문자열 연결입니다. 텍스트 출력에서는 숫자·불리언·enum 값도 표시 문자열로 바뀝니다." },
  { label: "빼기", value: "hp - 피해량", note: "피해, 비용, 남은 횟수 계산에 씁니다." },
  { label: "곱하기", value: "공격력 * 2", note: "치명타, 배율 보정, 가중치 계산에 씁니다." },
  { label: "나누기", value: "hp / maxHp", note: "비율, 평균, 진행률을 만들 때 씁니다." },
  { label: "나머지", value: "턴 % 2", note: "짝수 턴/홀수 턴처럼 주기를 나눌 때 씁니다." },
  { label: "제곱", value: "거리 ^ 2 또는 거리 ** 2", note: "난이도 곡선이나 거리 보정에 씁니다." },
  { label: "루트", value: "루트(위험도) 또는 sqrt(위험도)", note: "큰 값을 부드럽게 줄이는 보정에 씁니다." },
  { label: "괄호", value: "(hp + mp) / 2", note: "먼저 계산할 부분을 명확히 묶습니다." },
  { label: "비교", value: "호감도 >= 50", note: "@만약, 종료 조건, 조건부 규칙에서 씁니다." },
  { label: "그리고/또는", value: "hp > 0 그리고 열쇠 > 0", note: "한국어와 English alias(and/or)를 모두 지원합니다." },
  {
    label: "부정",
    value: "아님 ended",
    note: "ended가 참이면 끝난 상태, 아님 ended는 그 반대입니다. 즉 “아직 끝나지 않았으면”을 한 줄 조건으로 쓸 때 사용합니다.",
  },
  {
    label: "안전 함수",
    value: "범위(150, 0, 100)",
    note: "범위/clamp는 값을 최소~최대 안에 가둡니다. 예시는 150을 0~100 사이로 제한해 100이 됩니다. 루트·절댓값·반올림·최소·최대도 계산식 안에서 안전하게 쓸 수 있습니다.",
  },
];

const COMMAND_GUIDES: GuideRow[] = [
  {
    title: "게임 메타",
    ko: "@게임 제목=\"Sample Game\"",
    en: "@game title=\"Sample Game\"",
    description: ".rqt에서 제목, 설명, 아이콘을 선언하면 import 단계에서 game.meta로 내려갑니다. id/version은 아직 Stable 메타데이터가 아니며 패키지/공유 메타데이터로 분리됩니다.",
    sample: `#!roqeet/rqt 0.2

@게임 제목="Sample Game" 설명="Short pitch" 아이콘="map"`,
  },
  {
    title: "상태 스키마",
    ko: "@상태 체력 number 초기=100 최소=0 최대=100 변경주체=ai",
    en: "@state hp number initial=100 min=0 max=100 mutableBy=ai",
    description: "초기 상태, 타입, 변경 권한, 숫자 범위, enum 값, transient/derived 필드를 선언해 .rqg 검증 경로로 보냅니다.",
    sample: `#!roqeet/rqt 0.2

@game title="Schema Quest"
@state hp number initial=100 min=0 max=100 mutableBy=ai label="HP"
@state mood enum initial="normal" values="normal,tired,happy" mutableBy=ai
@state tempFlag boolean initial=false transient=true`,
  },
  {
    title: "상태 만들기",
    ko: "@설정 변수 = 변숫값",
    en: "@set variable = value",
    description: "게임이나 챗봇이 기억할 숫자, 글자, 참/거짓 값을 저장합니다.",
    sample: `#!roqeet/rqt 0.2

@설정 호감도 = 55
현재 호감도: @호감도`,
  },
  {
    title: "숫자 바꾸기",
    ko: "@증가 변수 값 / @감소 변수 값",
    en: "@increase variable value / @decrease variable value",
    description: "호감도, HP, 단서 수처럼 엔진이 판정해야 하는 숫자 상태를 명시적으로 바꿉니다.",
    sample: `#!roqeet/rqt 0.2

@설정 hp = 30
@감소 hp 5 최소 0
현재 HP: @hp`,
  },
  {
    title: "조건 나누기",
    ko: "@만약 조건 { ... } / @아니면 { ... }",
    en: "@if condition { ... } / @else { ... }",
    description: "상태에 따라 다른 문장이나 버튼을 보여줍니다.",
    sample: `#!roqeet/rqt 0.2

@설정 호감도 = 55

@만약 호감도 >= 50 {
  @캐릭터@은는 편하게 말한다.
}
@아니면 {
  아직 조심스럽게 말한다.
}`,
  },
  {
    title: "랜덤 이벤트",
    ko: "@랜덤 { ... }",
    en: "@random { ... }",
    description: "블록 안의 항목 중 하나를 골라 매번 다른 장면을 만들 수 있습니다.",
    sample: `#!roqeet/rqt 0.2

@랜덤 {
  바닥에서 낡은 쪽지를 발견했다.
  멀리서 발소리가 들린다.
}`,
  },
  {
    title: "AI 프롬프트",
    ko: "@프롬프트 \"이름\" { ... }",
    en: "@prompt \"name\" { ... }",
    description: "화면에는 보이지 않고 모델 입력에만 들어가는 숨은 지시문을 만듭니다.",
    sample: `#!roqeet/rqt 0.2

@설정 호감도 = 55

@프롬프트 "관계" {
  사용자: @유저
  호감도: @호감도
}

대화 준비 완료.`,
  },
  {
    title: "버튼",
    ko: "@버튼 \"라벨\" -> 동작",
    en: "@button \"label\" -> action",
    description: "제작자가 정의한 동작 이름을 가진 버튼을 표시합니다.",
    sample: `#!roqeet/rqt 0.2

문 앞에 섰다.
@버튼 "문 열기" -> open_door`,
  },
  {
    title: "이미지",
    ko: "@이미지 \"파일명\"",
    en: "@image \"asset\"",
    description: "장면이나 캐릭터에 연결할 이미지 에셋을 참조합니다.",
    sample: `#!roqeet/rqt 0.2

복도에 낮은 조명이 켜진다.
@이미지 "corridor.png"`,
  },
  {
    title: "대사",
    ko: "@대사 \"화자\" { ... }",
    en: "@dialogue \"speaker\" { ... }",
    description: "화자가 있는 문장을 안정 출력으로 분리합니다. AI가 아니라 엔진 출력으로 기록됩니다.",
    sample: `#!roqeet/rqt 0.2

@대사 "@캐릭터" {
  @유저, 오늘은 천천히 시작하자.
}`,
  },
  {
    title: "선택지",
    ko: "@선택지 { - 라벨 -> 동작 }",
    en: "@choices { - label -> action }",
    description: "플레이어가 고를 수 있는 선택지를 버튼 목록과 분리해 만듭니다.",
    sample: `#!roqeet/rqt 0.2

@선택지 {
  - 사과한다 -> apologize
  - 기다린다 -> wait
}`,
  },
  {
    title: "장면",
    ko: "@장면 \"id\" key=\"value\" { ... }",
    en: "@scene \"id\" key=\"value\" { ... }",
    description: "배경, 분위기 같은 장면 메타데이터와 그 안의 출력을 함께 기록합니다.",
    sample: `#!roqeet/rqt 0.2

@장면 "intro" 배경="room.png" 분위기="calm" {
  방 안은 조용하다.
}`,
  },
];

const LIFECYCLE: Record<LifecycleId, { label: string; title: string; body: string }> = {
  input: {
    label: "사용자 입력",
    title: "사용자가 메시지나 버튼을 누르는 순간",
    body: "상태와 최근 대화가 준비되고, 트리거 조건을 확인할 수 있는 출발점입니다.",
  },
  before: {
    label: "@응답전",
    title: "모델을 부르기 직전",
    body: "기억을 읽고, 상태를 점검하고, @프롬프트 섹션을 추가하기 좋은 단계입니다.",
  },
  model: {
    label: "모델 응답",
    title: "AI가 답변을 생성하는 동안",
    body: "@응답전에서 조립된 프롬프트와 현재 대화가 모델 입력으로 들어갑니다.",
  },
  after: {
    label: "@응답후",
    title: "모델 응답을 받은 직후",
    body: "응답 내용을 기억에 저장하거나 상태를 갱신하는 후처리 단계입니다.",
  },
  screen: {
    label: "화면 표시",
    title: "사용자에게 결과를 보여주는 순간",
    body: "최종 문장, 버튼, 이미지가 화면에 나타나고 다음 입력을 기다립니다.",
  },
};

const RQT_SPEC_GROUPS: SpecGroup[] = [
  {
    title: "파일과 메타",
    status: "Stable",
    body: "스크립트 버전, 주석, 실행 환경을 문서 맨 위와 블록 안에서 선언합니다.",
    examples: ["#!roqeet/rqt 0.2", "@game title=\"Sample Game\"", "@주석 ...", "@환경 { 언어: ko }"],
  },
  {
    title: "메타데이터와 상태 스키마",
    status: "Stable",
    body: ".rqt에서 game.meta, game.initialState, game.stateSchema, mutable 권한, enum 값, 숫자 clamp를 선언할 수 있습니다.",
    examples: [
      "@game title=\"Schema Quest\" description=\"Short pitch\" icon=\"map\"",
      "@state hp number initial=100 min=0 max=100 mutableBy=ai",
      "@state mood enum initial=\"normal\" values=\"normal,tired,happy\" mutableBy=ai",
      "@state tempFlag boolean initial=false transient=true",
    ],
  },
  {
    title: "상태와 분기",
    status: "Stable",
    body: "변수와 변숫값을 저장하고, 조건에 따라 다른 흐름을 실행합니다.",
    examples: ["@설정 hp = 30", "@증가 hp 5", "@설정 위험도 = 루트(소음)", "@만약 hp <= 0 { ... }", "@아니면 hp < 10 { ... }"],
  },
  {
    title: "출력과 UI",
    status: "Stable",
    body: "본문, 버튼, 이미지 참조를 만들어 챗봇이나 게임 화면에 보낼 수 있습니다.",
    examples: ['@대사 "하린" { ... }', "@선택지 { ... }", '@버튼 "문 열기" -> open_door', '@이미지 "corridor.png"'],
  },
  {
    title: "AI 프롬프트",
    status: "Stable",
    body: "사용자에게 보이지 않는 모델 지시문을 별도 섹션으로 조립합니다.",
    examples: ['@프롬프트 "캐릭터" { ... }', "@유저", "@캐릭터"],
  },
  {
    title: "게임 턴 명령",
    status: "Preview",
    body: "게임 응답 끝에 붙여 선택지, 상태 변화, 장면, 주사위를 실제 런타임 상태로 반영합니다.",
    examples: ["@선택지 { ... }", "@증가 clues 1", "@감소 hp 10", '@추가 inventory "열쇠"', "@장면 bg=forest mood=긴장", "@주사위 d20"],
  },
  {
    title: "훅과 기억",
    status: "Preview",
    body: "응답 전후 시점, 장기 기억, 트리거를 연결하는 고급 흐름입니다.",
    examples: ["@응답전 { ... }", "@응답후 { ... }", '@기억읽기 "최근_약속"', "@트리거 조건 { ... }"],
  },
  {
    title: "미디어와 장면",
    status: "Experimental",
    body: "배경, 음악, 효과음, 캐릭터 스프라이트, 표/테이블 같은 에셋 중심 명령입니다.",
    examples: ["@배경 \"cafe.png\"", '@음악 "theme.mp3"', "@스프라이트 하린 smile", "@표 관계도 { ... }"],
  },
  {
    title: "함수와 테스트",
    status: "Experimental",
    body: "반복되는 흐름을 재사용하고, 제작자가 동작을 검증하기 위한 문법입니다.",
    examples: ["@함수 인사(이름) { ... }", "@불러오기 common.rqt", "@테스트 \"엔딩\" { ... }", "@로그 상태"],
  },
  {
    title: "제거된 블록 종료",
    status: "Removed",
    body: "0.2 공식 문법은 { } 블록만 사용합니다. 과거 문법은 importer나 migration에서만 다룹니다.",
    examples: ["@끝: Removed", "@end: Removed"],
  },
];

const JSON_SPEC_GROUPS: SpecGroup[] = [
  {
    title: "문서 루트",
    status: "Stable",
    body: ".rqg 저장과 검증의 최상위 단위입니다. 사람이 쓴 .rqt를 런타임이 실행하기 좋은 게임 문서로 정규화합니다.",
    examples: ['kind: "roqeet.game"', 'rqtVersion: "0.1"', 'formatVersion: "0.1"', "nodes: [...]"],
  },
  {
    title: "게임 문서 노드",
    status: "Stable",
    body: "메타데이터, 초기 상태, AI 계약, 규칙, 에셋, HUD가 선언형 노드로 분리됩니다.",
    examples: ["game.meta", "game.initialState", "game.aiContract", "game.rules", "game.assets", "game.hud"],
  },
  {
    title: "식 표현",
    status: "Stable",
    body: ".rqt 계산식과 .rqg 조건 값은 검증 가능한 데이터로 내려갑니다. 제작 화면에서는 연산식으로 부릅니다. 사칙연산, 제곱, 루트, 비교, 논리식을 지원합니다.",
    examples: ['{ rex: "호감도 >= 50" }', '{ rex: "hp - 피해량" }', '{ rex: "루트(위험도) * 10" }', '{ rex: "거리 ^ 2" }'],
  },
  {
    title: "스키마 검증",
    status: "Preview",
    body: "문서에 없는 노드, 잘못된 타입, 누락된 필수 값을 에디터와 가져오기 단계에서 잡습니다.",
    examples: ["node.id", "sourceMap", "diagnostics", "schemaVersion"],
  },
  {
    title: ".rqp",
    status: "Stable",
    body: "스크립트, 에셋, 권한, 메타데이터를 묶는 배포 단위입니다. 실행 문법이 아니라 공유/설치 포맷입니다.",
    examples: ["manifest.json", "game.rqg", "source/main.rqt", "assets/...", "permissions"],
  },
  {
    title: "권한과 에셋",
    status: "Stable",
    body: "외부 호출, 저장소 접근, 미디어 파일 참조처럼 제작물 배포 때 확인해야 하는 정보를 담습니다.",
    examples: ["network.fetch", "storage.session", "model.chat", "contentRating.requiresAgeGate"],
  },
];

export function RoqeetGuideView() {
  const [source, setSource] = useState(FIVE_MINUTE_RQT_SAMPLE);
  const [mode, setMode] = useState<GuideMode>("rqt");
  const [referenceScope, setReferenceScope] = useState<"stable" | "all">("stable");
  const [result, setResult] = useState(() =>
    runRoqeet(FIVE_MINUTE_RQT_SAMPLE, RUN_CONTEXT, {
      fileId: "guide/main.rqt",
      invalidationReason: "initial_preview",
    }),
  );
  const sourceRef = useRef(source);
  const rangeRef = useRef<RoqeetChangedRange>({ from: 0, to: 0, insertedText: source });
  const schedulerRef = useRef<LatestJobScheduler<ReturnType<typeof runRoqeet>> | null>(null);
  if (!schedulerRef.current) {
    schedulerRef.current = new LatestJobScheduler({
      debounceMs: 40,
      run: ({ signal, invalidationReason }) => {
        if (signal.aborted) throw new DOMException("Preview job was cancelled.", "AbortError");
        return runRoqeetChangedRange(sourceRef.current, rangeRef.current, RUN_CONTEXT, {
          fileId: "guide/main.rqt",
          invalidationReason,
          detail: { previewMode: "guide" },
        });
      },
      onResult: (next) => setResult(next),
    });
  }
  const json = useMemo(() => JSON.stringify(result.json, null, 2), [result.json]);
  const fiveMinuteResult = useMemo(
    () => runRoqeet(FIVE_MINUTE_RQT_SAMPLE, RUN_CONTEXT, {
      fileId: "guide/five-minute.rqt",
      invalidationReason: "guide_static_preview",
    }),
    [],
  );
  const assetbotResult = useMemo(
    () => runRoqeet(ASSETBOT_STARTER_SAMPLE, RUN_CONTEXT, {
      fileId: "guide/first-assetbot.rqt",
      invalidationReason: "guide_static_preview",
    }),
    [],
  );

  useEffect(() => () => schedulerRef.current?.dispose(), []);

  const updateSource = (next: string, invalidationReason: string) => {
    rangeRef.current = diffRange(sourceRef.current, next);
    sourceRef.current = next;
    setSource(next);
    schedulerRef.current?.schedule(invalidationReason);
  };
  const loadSample = (next: string) => {
    setMode("rqt");
    updateSource(next, "guide_sample_load");
  };
  const jumpTo = (id: string) => {
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-4 scroll-touch">
      <div className="mx-auto flex max-w-7xl flex-col gap-6">
        <GuideHero onJump={jumpTo} onLoadExample={() => loadSample(FIVE_MINUTE_RQT_SAMPLE)} />
        <GuideToc onJump={jumpTo} />
        <QuickMapSection onJump={jumpTo} />
        <ProblemsSection />
        <FiveMinuteSection result={fiveMinuteResult} onLoadExample={() => loadSample(FIVE_MINUTE_RQT_SAMPLE)} />
        <FirstAssetBotSection result={assetbotResult} onLoadExample={() => loadSample(ASSETBOT_STARTER_SAMPLE)} />
        <RqtExamplesSection onLoadSource={loadSample} />
        <TerminologySection />
        <CoreConceptsSection />
        <SaveAndValidationSection />
        <SyntaxReferenceSection
          scope={referenceScope}
          setScope={setReferenceScope}
          setSource={loadSample}
        />
        <CommandIndexSection />
        <RecipesSection />
        <PackageAuthoringSection />
        <LanguageModesSection />
        <AdvancedSection />
        <SyntaxLabSection
          mode={mode}
          setMode={setMode}
          source={source}
          json={json}
          result={result}
          updateSource={updateSource}
          setSource={loadSample}
        />
      </div>
    </div>
  );
}

function diffRange(previous: string, next: string): RoqeetChangedRange {
  let from = 0;
  const min = Math.min(previous.length, next.length);
  while (from < min && previous[from] === next[from]) from++;

  let previousEnd = previous.length;
  let nextEnd = next.length;
  while (previousEnd > from && nextEnd > from && previous[previousEnd - 1] === next[nextEnd - 1]) {
    previousEnd--;
    nextEnd--;
  }

  return { from, to: previousEnd, insertedText: next.slice(from, nextEnd) };
}

function RuntimePreview({ result }: { result: ReturnType<typeof runRoqeet> }) {
  return (
    <div className="grid min-w-0 gap-3 sm:grid-cols-2 lg:grid-cols-1">
      <OutputBlock icon={<Play size={16} aria-hidden />} title="실행 결과">
        <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-6 text-ink">
          {result.text || "(출력 없음)"}
        </pre>
      </OutputBlock>

      <OutputBlock icon={<MessageSquare size={16} aria-hidden />} title="프롬프트">
        {result.prompts.length ? (
          <div className="space-y-2">
            {result.prompts.map((prompt) => (
              <div key={prompt.name}>
                <div className="text-[11px] font-semibold text-ink-faint">{prompt.name}</div>
                <pre className="whitespace-pre-wrap break-words font-sans text-xs leading-5 text-ink">
                  {prompt.content}
                </pre>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-sm text-ink-faint">(없음)</span>
        )}
      </OutputBlock>

      <OutputBlock icon={<MessageSquare size={16} aria-hidden />} title="대사">
        {result.dialogues.length ? (
          <div className="space-y-2">
            {result.dialogues.map((dialogue, index) => (
              <div key={`${dialogue.speaker}:${index}`}>
                <div className="text-[11px] font-semibold text-ink-faint">{dialogue.speaker || "화자 없음"}</div>
                <pre className="whitespace-pre-wrap break-words font-sans text-xs leading-5 text-ink">
                  {dialogue.content}
                </pre>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-sm text-ink-faint">(없음)</span>
        )}
      </OutputBlock>

      <OutputBlock icon={<SquareMousePointer size={16} aria-hidden />} title="UI">
        {result.ui.length ? (
          <div className="flex flex-wrap gap-2">
            {result.ui.map((item) => (
              <button
                key={`${item.label}:${item.action}`}
                className="rounded-lg border border-line px-2 py-1 text-xs text-ink-soft"
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : (
          <span className="text-sm text-ink-faint">(없음)</span>
        )}
      </OutputBlock>

      <OutputBlock icon={<Image size={16} aria-hidden />} title="미디어">
        {result.media.length ? (
          <div className="flex flex-wrap gap-1.5">
            {result.media.map((item) => (
              <code
                key={item.asset}
                className="rounded border border-line bg-bg-raised px-2 py-1 text-xs text-accent-soft"
              >
                {item.asset}
              </code>
            ))}
          </div>
        ) : (
          <span className="text-sm text-ink-faint">(없음)</span>
        )}
      </OutputBlock>

      <OutputBlock icon={<Braces size={16} aria-hidden />} title="장면">
        {result.scenes.length ? (
          <div className="space-y-2">
            {result.scenes.map((scene) => (
              <div key={scene.id} className="rounded border border-line bg-bg-raised p-2">
                <div className="text-xs font-semibold text-accent-soft">{scene.id}</div>
                <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-4 text-ink-soft">
                  {JSON.stringify(scene.props, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-sm text-ink-faint">(없음)</span>
        )}
      </OutputBlock>
    </div>
  );
}

function JsonPreview({ json }: { json: string }) {
  return (
    <div className="rounded-lg border border-line bg-bg-soft p-3">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink">
        <Braces size={16} aria-hidden className="text-accent-soft" />
        구조 미리보기
      </div>
      <div className="space-y-3 text-sm leading-6 text-ink-soft">
        <p>`.rqg`은 편집기, 검증기, 엔진이 읽기 쉬운 표준 구조입니다.</p>
        <p>왼쪽 JSON은 현재 `main.rqt`를 정규화한 결과이며, 각 node가 실행 단위가 됩니다.</p>
      </div>
      <div className="mt-3 rounded border border-line bg-bg-raised p-2 text-xs text-ink-faint">
        {json.includes("\"type\": \"if\"") ? "조건 노드 포함" : "조건 노드 없음"} ·{" "}
        {json.includes("\"type\": \"prompt\"") ? "프롬프트 노드 포함" : "프롬프트 노드 없음"}
      </div>
    </div>
  );
}

function GuideHero({
  onJump,
  onLoadExample,
}: {
  onJump: (id: string) => void;
  onLoadExample: () => void;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-line bg-bg-soft">
      <div className="grid gap-4 p-5 lg:grid-cols-[minmax(0,1.05fr)_minmax(18rem,0.95fr)] lg:p-6">
        <div className="min-w-0">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3 py-1 text-xs font-medium text-accent-soft">
            <Gamepad2 size={14} aria-hidden />
            AI game runtime language
          </div>
          <h2 className="text-2xl font-semibold leading-tight text-ink sm:text-3xl">RoQeet Guide</h2>
          <p className="mt-2 text-base font-medium text-ink">
            AI 채팅을 게임처럼 만드는 상태·선택지·에셋 언어
          </p>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-ink-soft">
            프롬프트와 정규식에 숨기던 게임 로직을 .rqt 문법과 RoQeet 런타임으로 분리한다.
            LLM은 대사와 분위기를 만들고, RoQeet은 상태 계산, 선택지, 에셋, UI, 세이브를 검증 가능한 구조로 관리합니다.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-soft"
              onClick={() => {
                onLoadExample();
                onJump("five-minute");
              }}
            >
              <Play size={16} aria-hidden />
              5분 예제 보기
            </button>
            <button
              className="inline-flex items-center gap-2 rounded-lg border border-line px-4 py-2 text-sm text-ink-soft hover:bg-bg-raised hover:text-ink"
              onClick={() => onJump("syntax-reference")}
            >
              <BookOpen size={16} aria-hidden />
              문법 레퍼런스 보기
            </button>
          </div>
        </div>
        <div className="grid gap-3 rounded-lg border border-line bg-bg-raised p-3">
          <div>
            <div className="text-xs font-semibold text-ink-faint">핵심 문장</div>
            <p className="mt-1 text-sm leading-6 text-ink">
              RoQeet은 AI에게 상태 태그와 UI 태그를 억지로 출력시키는 대신,
              상태·선택지·에셋·UI·세이브를 런타임이 직접 관리하게 만드는 게임형 LLM 창작 엔진입니다.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <SyntaxLine label=".rqt" value="사람이 읽고 쓰는 창작 문법" />
            <SyntaxLine label=".rqg" value="에디터와 엔진이 실행하는 구조화 결과" />
          </div>
        </div>
      </div>
    </section>
  );
}

function GuideToc({ onJump }: { onJump: (id: string) => void }) {
  return (
    <nav className="sticky top-0 z-10 rounded-lg border border-line bg-bg/95 p-2 backdrop-blur">
      <div className="flex gap-1 overflow-x-auto">
        {GUIDE_ANCHORS.map((item) => (
          <button
            key={item.id}
            className="shrink-0 rounded-md px-3 py-1.5 text-xs text-ink-soft hover:bg-bg-raised hover:text-ink"
            onClick={() => onJump(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

function QuickMapSection({ onJump }: { onJump: (id: string) => void }) {
  return (
    <section id="quick-map" className="scroll-mt-16 rounded-lg border border-line bg-bg-soft p-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-normal text-accent-soft">Quick Start</div>
          <h3 className="mt-1 text-lg font-semibold text-ink">처음 3분에 볼 것</h3>
          <p className="mt-2 text-sm leading-6 text-ink-soft">
            RoQeet은 AI에게 태그를 맞히게 하는 도구가 아니라, 대사와 게임 규칙을 분리하는 런타임입니다.
            처음에는 예제 하나를 실행하고, 상태가 어떻게 바뀌는지 확인한 다음, 명령 색인에서 필요한 명령만 찾아 쓰면 됩니다.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              className="rounded-lg border border-accent bg-accent/15 px-3 py-1.5 text-xs font-medium text-ink hover:bg-accent/25"
              onClick={() => onJump("five-minute")}
            >
              완성 예제 보기
            </button>
            <button
              className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft hover:bg-bg-raised hover:text-ink"
              onClick={() => onJump("terminology")}
            >
              용어 먼저 보기
            </button>
            <button
              className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft hover:bg-bg-raised hover:text-ink"
              onClick={() => onJump("command-index")}
            >
              명령 색인 보기
            </button>
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <SyntaxLine label="LLM" value="대사, 묘사, 분위기, 다음 행동 제안" />
          <SyntaxLine label="RoQeet Runtime" value="상태 계산, 검증, 선택지 실행, 에셋 표시" />
          <SyntaxLine label="작성 시작점" value=".rqt 0.2 한글 명령" />
          <SyntaxLine label="공유/검증 단위" value=".rqg / .rqp" />
        </div>
      </div>
    </section>
  );
}

function ProblemsSection() {
  return (
    <GuideSection
      id="problems"
      eyebrow="Why RoQeet"
      title="RoQeet이 해결하는 문제"
      body="AI 채팅 플랫폼에서 정규식, 숨김 태그, Lua 조각으로 처리하던 게임 기능을 명시적인 런타임 기능으로 옮깁니다."
    >
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(20rem,0.65fr)]">
        <div className="overflow-x-auto rounded-lg border border-line">
          <div className="min-w-[52rem]">
            <div className="grid grid-cols-[minmax(10rem,0.9fr)_minmax(10rem,1fr)_minmax(10rem,1fr)] border-b border-line bg-bg-raised text-xs font-semibold text-ink-faint">
            <div className="px-3 py-2">기존 방식</div>
            <div className="px-3 py-2">깨지는 이유</div>
            <div className="px-3 py-2">RoQeet 방식</div>
          </div>
            {PROBLEM_ROWS.map((row) => (
            <div
              key={row.oldWay}
              className="grid grid-cols-[minmax(10rem,0.9fr)_minmax(10rem,1fr)_minmax(10rem,1fr)] border-b border-line last:border-b-0"
            >
              <div className="px-3 py-2 text-xs leading-5 text-ink-soft">{row.oldWay}</div>
              <div className="px-3 py-2 text-xs leading-5 text-ink-faint">{row.risk}</div>
              <div className="px-3 py-2 text-xs leading-5 text-accent-soft">{row.roqeetWay}</div>
            </div>
            ))}
          </div>
        </div>
        <div className="grid gap-3">
          <ResponsibilityBlock
            title="LLM이 하는 일"
            items={["대사", "묘사", "캐릭터 반응", "분위기", "다음 행동 제안"]}
          />
          <ResponsibilityBlock
            title="RoQeet 런타임이 하는 일"
            items={["상태 계산", "최소/최대 보장", "선택지 실행", "에셋 표시", "UI/HUD", "세이브/로드", "검증과 오류 표시"]}
          />
          <div className="rounded-lg border border-line bg-bg-soft p-3">
            <h4 className="text-sm font-semibold text-ink">.rqt와 .rqg</h4>
            <p className="mt-2 text-xs leading-5 text-ink-soft">
              창작자는 .rqt를 씁니다. RoQeet은 이를 .rqg으로 변환해 에디터 저장, 검증, 가져오기,
              런타임 실행에 같은 구조를 쓰게 만듭니다.
            </p>
          </div>
        </div>
      </div>
    </GuideSection>
  );
}

function ResponsibilityBlock({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-lg border border-line bg-bg-soft p-3">
      <h4 className="text-sm font-semibold text-ink">{title}</h4>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {items.map((item) => (
          <span key={item} className="rounded border border-line bg-bg-raised px-2 py-1 text-xs text-ink-soft">
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function FiveMinuteSection({
  result,
  onLoadExample,
}: {
  result: ReturnType<typeof runRoqeet>;
  onLoadExample: () => void;
}) {
  return (
    <GuideSection
      id="five-minute"
      eyebrow="First Success"
      title="5분 예제"
      body="상태, 프롬프트, 장면, 선택지, 대사, 에셋 표시가 한 번에 분리되는 작은 에셋봇 예제입니다."
    >
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,0.9fr)]">
        <div className="grid gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs leading-5 text-ink-faint">
              아래 예제는 현재 실행 가능한 .rqt 0.2 문법만 사용합니다.
            </div>
            <button
              className="inline-flex items-center gap-2 rounded-lg border border-accent bg-accent/15 px-3 py-1.5 text-xs font-medium text-ink hover:bg-accent/25"
              onClick={onLoadExample}
            >
              <FileText size={14} aria-hidden />
              편집기에서 열기
            </button>
          </div>
          <CodeBlock title="현재 실행 가능한 .rqt 0.2" value={FIVE_MINUTE_RQT_SAMPLE} />
        </div>
        <FiveMinuteResultPanel result={result} />
      </div>
    </GuideSection>
  );
}

function FiveMinuteResultPanel({ result }: { result: ReturnType<typeof runRoqeet> }) {
  const stateText = JSON.stringify(result.state, null, 2);
  const promptText = result.prompts.map((item) => `[${item.name}]\n${item.content}`).join("\n\n");
  const assetText = result.media.map((item) => item.asset).join("\n");
  return (
    <div className="grid gap-3">
      <OutputBlock icon={<Play size={16} aria-hidden />} title="실행 결과">
        <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-6 text-ink">
          {result.text || "(출력 없음)"}
        </pre>
      </OutputBlock>
      <OutputBlock icon={<Database size={16} aria-hidden />} title="상태 변경">
        <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-5 text-ink">
          {stateText}
        </pre>
      </OutputBlock>
      <OutputBlock icon={<Image size={16} aria-hidden />} title="표시 에셋">
        <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-5 text-accent-soft">
          {assetText || "(없음)"}
        </pre>
      </OutputBlock>
      <OutputBlock icon={<MessageSquare size={16} aria-hidden />} title="생성 프롬프트">
        <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words font-sans text-xs leading-5 text-ink">
          {promptText || "(없음)"}
        </pre>
      </OutputBlock>
    </div>
  );
}

function FirstAssetBotSection({
  result,
  onLoadExample,
}: {
  result: ReturnType<typeof runRoqeet>;
  onLoadExample: () => void;
}) {
  const loadAssetbot = (_value: string) => onLoadExample();
  const stateText = JSON.stringify(result.state, null, 2);
  const promptText = result.prompts.map((item) => `[${item.name}]\n${item.content}`).join("\n\n");
  const sceneText = result.scenes.map((item) => `${item.id}: ${JSON.stringify(item.props)}`).join("\n");
  return (
    <GuideSection
      id="first-assetbot"
      eyebrow="Onboarding"
      title="첫 번째 에셋봇 만들기"
      body="리포트에서 남은 가장 큰 공백은 연속 튜토리얼입니다. 여기서는 캐릭터 정보, 상태, 에셋 키, 선택지, 저장 관점을 한 흐름으로 묶습니다."
    >
      <div className="grid gap-4">
        <BeginnerGuide setSource={loadAssetbot} />
        <AssetBotFiveMinuteGuide setSource={loadAssetbot} />
        <section className="grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
          <div className="grid gap-3">
            <CommonConfusionGuide />
            <div className="rounded-lg border border-line bg-bg-soft p-3">
              <div className="mb-3 flex items-center gap-2">
                <CheckCircle2 size={16} aria-hidden className="text-accent-soft" />
                <h3 className="text-sm font-semibold text-ink">완성 체크리스트</h3>
              </div>
              <div className="grid gap-2 text-xs leading-5 text-ink-soft">
                <SyntaxLine label="상태" value="호감도, 표정, 배경처럼 다음 턴에 남을 값이 있는가?" />
                <SyntaxLine label="에셋" value="AI 텍스트 토큰이 아니라 표정/배경 키로 이미지가 결정되는가?" />
                <SyntaxLine label="프롬프트" value="LLM에게 말투와 분위기만 주고 규칙 집행은 엔진에 맡겼는가?" />
                <SyntaxLine label="선택지" value="플레이어 입력이 네이티브 버튼/action으로 분리되는가?" />
              </div>
            </div>
          </div>
          <div className="grid gap-3">
            <OutputBlock icon={<Play size={16} aria-hidden />} title="템플릿 실행 미리보기">
              <OutputMini result={result} />
            </OutputBlock>
            <OutputBlock icon={<Database size={16} aria-hidden />} title="상태 스냅샷">
              <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-5 text-ink">
                {stateText}
              </pre>
            </OutputBlock>
            <OutputBlock icon={<MessageSquare size={16} aria-hidden />} title="생성 프롬프트와 장면">
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words font-sans text-xs leading-5 text-ink">
                {[promptText, sceneText].filter(Boolean).join("\n\n") || "(없음)"}
              </pre>
            </OutputBlock>
          </div>
        </section>
      </div>
    </GuideSection>
  );
}

function TerminologySection() {
  return (
    <GuideSection
      id="terminology"
      eyebrow="Glossary"
      title="처음 보는 용어"
      body="가이드에서 반복되는 줄임말과 포맷 이름을 먼저 정리합니다. 초보자는 이 표만 보고 5분 예제로 넘어가도 됩니다."
    >
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {GLOSSARY_ROWS.map((row) => (
          <article key={row.term} className="min-w-0 rounded-lg border border-line bg-bg-soft p-3">
            <h4 className="text-sm font-semibold text-ink">{row.term}</h4>
            <p className="mt-2 text-xs leading-5 text-ink-soft">{row.meaning}</p>
            <code className="mt-3 block overflow-x-auto rounded border border-line bg-bg-raised px-2 py-1.5 text-xs leading-5 text-accent-soft">
              {row.example}
            </code>
          </article>
        ))}
      </div>
    </GuideSection>
  );
}

function CoreConceptsSection() {
  return (
    <GuideSection
      id="concepts"
      eyebrow="Core"
      title="핵심 개념"
      body="RoQeet은 기능 이름을 외우는 문법이 아니라, AI가 잘하는 일과 런타임이 보장할 일을 나누는 모델입니다."
    >
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {CONCEPT_CARDS.map((card) => (
          <article key={card.title} className="min-w-0 rounded-lg border border-line bg-bg-soft p-3">
            <h4 className="text-sm font-semibold text-ink">{card.title}</h4>
            <p className="mt-2 text-xs leading-5 text-ink-soft">{card.body}</p>
            <pre className="mt-3 overflow-x-auto whitespace-pre rounded border border-line bg-bg-raised p-2 font-mono text-[11px] leading-4 text-accent-soft">
              {card.example}
            </pre>
          </article>
        ))}
      </div>
    </GuideSection>
  );
}

function SaveAndValidationSection() {
  return (
    <GuideSection
      id="save-validation"
      eyebrow="Safety"
      title="저장·로드와 검증 오류"
      body="RoQeet은 HTML 주석이나 대화 로그에 세이브를 숨기지 않습니다. 게임 카드는 구조화된 정의로 저장하고, 가져오기와 편집 단계에서 검증합니다."
    >
      <div className="grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <section className="rounded-lg border border-line bg-bg-soft p-3">
          <div className="mb-3 flex items-center gap-2">
            <Database size={16} aria-hidden className="text-accent-soft" />
            <h3 className="text-sm font-semibold text-ink">저장/공유 흐름</h3>
          </div>
          <div className="grid gap-3">
            {SAVE_FLOW_ROWS.map((row) => (
              <article key={row.title} className="rounded-lg border border-line bg-bg-raised p-3">
                <h4 className="text-sm font-semibold text-ink">{row.title}</h4>
                <div className="mt-2 grid gap-2 text-xs leading-5">
                  <SyntaxLine label="사용자 행동" value={row.userAction} />
                  <SyntaxLine label="저장 단위" value={row.storedAs} />
                  <p className="text-ink-soft">{row.note}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="rounded-lg border border-line bg-bg-soft p-3">
          <div className="mb-3 flex items-center gap-2">
            <ShieldAlert size={16} aria-hidden className="text-accent-soft" />
            <h3 className="text-sm font-semibold text-ink">검증 오류를 고치는 법</h3>
          </div>
          <div className="grid gap-3">
            {DIAGNOSTIC_ROWS.map((row) => (
              <article key={row.issue} className="rounded-lg border border-line bg-bg-raised p-3">
                <h4 className="text-sm font-semibold text-ink">{row.issue}</h4>
                <div className="mt-2 grid gap-2 text-xs leading-5">
                  <p className="rounded border border-amber-800/50 bg-amber-950/25 px-2 py-1.5 text-amber-200">
                    {row.message}
                  </p>
                  <p className="rounded border border-emerald-800/50 bg-emerald-950/25 px-2 py-1.5 text-emerald-200">
                    {row.fix}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </GuideSection>
  );
}

function CommandIndexSection() {
  return (
    <GuideSection
      id="command-index"
      eyebrow="Index"
      title="주요 명령어 색인"
      body="레퍼런스 전체를 펼치기 전에, 가장 자주 쓰는 명령을 역할별로 훑어보는 짧은 표입니다."
      defaultOpen={false}
    >
      <div className="overflow-x-auto rounded-lg border border-line">
        <div className="min-w-[44rem]">
          <div className="grid grid-cols-[10rem_minmax(10rem,0.85fr)_minmax(16rem,1.15fr)] border-b border-line bg-bg-raised text-xs font-semibold text-ink-faint">
            <div className="px-3 py-2">명령</div>
            <div className="px-3 py-2">역할</div>
            <div className="px-3 py-2">언제 쓰나</div>
          </div>
          {COMMAND_INDEX_ROWS.map((row) => (
            <div
              key={row.command}
              className="grid grid-cols-[10rem_minmax(10rem,0.85fr)_minmax(16rem,1.15fr)] border-b border-line last:border-b-0"
            >
              <code className="px-3 py-2 text-xs leading-5 text-accent-soft">{row.command}</code>
              <div className="px-3 py-2 text-xs leading-5 text-ink-soft">{row.role}</div>
              <div className="px-3 py-2 text-xs leading-5 text-ink-faint">{row.useWhen}</div>
            </div>
          ))}
        </div>
      </div>
    </GuideSection>
  );
}

function SyntaxReferenceSection({
  scope,
  setScope,
  setSource,
}: {
  scope: "stable" | "all";
  setScope: (scope: "stable" | "all") => void;
  setSource: (value: string) => void;
}) {
  const groups = RQT_SPEC_GROUPS.filter((group) =>
    scope === "stable"
      ? group.status === "Stable"
      : group.status !== "Experimental" && group.status !== "Removed",
  );
  return (
    <GuideSection
      id="syntax-reference"
      eyebrow="Reference"
      title="문법 레퍼런스"
      body="초보자용 기본 탭은 Stable 기능만 먼저 보여줍니다. Preview 기능은 전체 탭에서 확인하고, Experimental 기능은 고급 섹션으로 분리했습니다."
    >
      <div className="mb-3 flex flex-wrap gap-2">
        <SegmentButton active={scope === "stable"} onClick={() => setScope("stable")}>
          <CheckCircle2 size={16} aria-hidden />
          기본 Stable
        </SegmentButton>
        <SegmentButton active={scope === "all"} onClick={() => setScope("all")}>
          <Braces size={16} aria-hidden />
          전체 레퍼런스
        </SegmentButton>
      </div>
      <div className="grid gap-4">
        <SpecCoverageGuide title="문서 범위 지도" groups={groups} />
        <section className="rounded-lg border border-line bg-bg-soft p-3">
          <div className="mb-3 flex items-center gap-2">
            <Code2 size={16} aria-hidden className="text-accent-soft" />
            <h3 className="text-sm font-semibold text-ink">명령: 한글 문법과 English Expert Mode</h3>
          </div>
          <div className="grid gap-3 xl:grid-cols-2">
            {COMMAND_GUIDES.map((guide) => (
              <CommandCard key={guide.title} guide={guide} setSource={setSource} />
            ))}
          </div>
        </section>
        <ExpressionGuide />
      </div>
    </GuideSection>
  );
}

function RecipesSection() {
  return (
    <GuideSection
      id="recipes"
      eyebrow="Recipes"
      title="레시피"
      body="실전 제작자는 명령 이름보다 패턴을 먼저 찾습니다. 자주 쓰는 시스템을 바로 가져다 바꿀 수 있게 모았습니다."
      defaultOpen={false}
    >
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {RECIPE_CARDS.map((recipe) => (
          <article key={recipe.title} className="min-w-0 rounded-lg border border-line bg-bg-soft p-3">
            <h4 className="text-sm font-semibold text-ink">{recipe.title}</h4>
            <p className="mt-2 text-xs leading-5 text-ink-soft">{recipe.body}</p>
            <pre className="mt-3 overflow-x-auto whitespace-pre rounded border border-line bg-bg-raised p-2 font-mono text-[11px] leading-4 text-accent-soft">
              {recipe.example}
            </pre>
          </article>
        ))}
      </div>
    </GuideSection>
  );
}

function PackageAuthoringSection() {
  return (
    <GuideSection
      id="package-authoring"
      eyebrow="Share"
      title=".rqp으로 공유하기"
      body="설치 미리보기 UI는 이미 있지만, 제작자에게는 최소 패키지 구조와 manifest 예제가 필요합니다. 아래 형태를 기준으로 압축하면 설치 전에 검증됩니다."
      defaultOpen={false}
    >
      <div className="grid gap-4 xl:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)]">
        <div className="grid gap-3">
          <CodeBlock title="최소 폴더 구조" value={PACKAGE_TREE_SAMPLE} />
          <div className="rounded-lg border border-line bg-bg-soft p-3">
            <div className="mb-2 flex items-center gap-2">
              <Package size={16} aria-hidden className="text-accent-soft" />
              <h3 className="text-sm font-semibold text-ink">설치 전 검증</h3>
            </div>
            <div className="grid gap-2 text-xs leading-5 text-ink-soft">
              <SyntaxLine label="필수 파일" value="루트 manifest.json + game.rqg" />
              <SyntaxLine label="필수 entry" value="entry.game가 패키지 안의 .rqg를 가리켜야 함" />
              <SyntaxLine label="안전 경로" value="../, 절대 경로, 빈 경로는 거부" />
              <SyntaxLine label="권한" value="민감 권한은 설치 전에 allow/ask/deny로 표시" />
            </div>
          </div>
        </div>
        <CodeBlock title="manifest.json 예시" value={PACKAGE_MANIFEST_SAMPLE} />
      </div>
    </GuideSection>
  );
}

function LanguageModesSection() {
  return (
    <GuideSection
      id="language-modes"
      eyebrow="Authoring"
      title="언어 모드"
      body="현재 공식 가이드는 실행 가능한 .rqt 0.2 명령 표면만 설명합니다. 한글 명령, English Expert Mode, LLM action 출력용 명령은 같은 런타임 계약으로 검증됩니다."
      defaultOpen={false}
    >
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {LANGUAGE_MODE_ROWS.map((row) => (
          <article key={row.mode} className="min-w-0 rounded-lg border border-line bg-bg-soft p-3">
            <h4 className="text-sm font-semibold text-ink">{row.mode}</h4>
            <p className="mt-2 text-xs leading-5 text-ink-soft">{row.useWhen}</p>
            <pre className="mt-3 overflow-x-auto whitespace-pre rounded border border-line bg-bg-raised p-2 font-mono text-[11px] leading-4 text-accent-soft">
              {row.example}
            </pre>
            <p className="mt-3 text-xs leading-5 text-ink-faint">{row.note}</p>
          </article>
        ))}
      </div>
    </GuideSection>
  );
}

function AdvancedSection() {
  const experimentalGroups = RQT_SPEC_GROUPS.filter((group) => group.status === "Experimental");
  return (
    <GuideSection
      id="advanced"
      eyebrow="Advanced"
      title="고급"
      body=".rqg, .rqp, 권한, 응답 훅, 마이그레이션, Experimental 기능은 초보 흐름과 분리했습니다."
      defaultOpen={false}
    >
      <div className="grid gap-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {ADVANCED_CARDS.map((card) => (
            <article key={card.title} className="rounded-lg border border-line bg-bg-soft p-3">
              <h4 className="text-sm font-semibold text-ink">{card.title}</h4>
              <p className="mt-2 text-xs leading-5 text-ink-soft">{card.body}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {card.examples.map((example) => (
                  <code key={example} className="rounded border border-line bg-bg-raised px-2 py-1 text-xs text-accent-soft">
                    {example}
                  </code>
                ))}
              </div>
            </article>
          ))}
        </div>
        <section className="grid gap-4 xl:grid-cols-2">
          <JsonGuide />
          <LifecycleGuide />
        </section>
        <RqtPackInstallLab />
        {experimentalGroups.length > 0 && (
          <SpecCoverageGuide title="Experimental 기능" groups={experimentalGroups} />
        )}
      </div>
    </GuideSection>
  );
}

function SyntaxLabSection({
  mode,
  setMode,
  source,
  json,
  result,
  updateSource,
  setSource,
}: {
  mode: GuideMode;
  setMode: (mode: GuideMode) => void;
  source: string;
  json: string;
  result: ReturnType<typeof runRoqeet>;
  updateSource: (next: string, invalidationReason: string) => void;
  setSource: (value: string) => void;
}) {
  return (
    <GuideSection
      id="syntax-lab"
      eyebrow="Playground"
      title="문법 실험"
      body="기존 플레이그라운드는 이 섹션으로 내렸습니다. 샘플을 바꾸며 .rqt 실행 결과와 .rqg 구조를 비교할 수 있습니다."
      defaultOpen={false}
    >
      <div className="mb-3 flex flex-wrap gap-2">
        <SegmentButton active={mode === "rqt"} onClick={() => setMode("rqt")}>
          <FileText size={16} aria-hidden />
          .rqt
        </SegmentButton>
        <SegmentButton active={mode === "json"} onClick={() => setMode("json")}>
          <Braces size={16} aria-hidden />
          .rqg
        </SegmentButton>
        <SegmentButton active={false} onClick={() => setSource(CHATBOT_SAMPLE)}>
          <Bot size={16} aria-hidden />
          챗봇
        </SegmentButton>
        <SegmentButton active={false} onClick={() => setSource(GAME_SAMPLE)}>
          <Gamepad2 size={16} aria-hidden />
          게임
        </SegmentButton>
        <SegmentButton active={false} onClick={() => setSource(FIVE_MINUTE_RQT_SAMPLE)}>
          <CheckCircle2 size={16} aria-hidden />
          5분 예제
        </SegmentButton>
      </div>
      <section className="grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(20rem,0.95fr)]">
        <div className="flex min-h-[430px] min-w-0 flex-col rounded-lg border border-line bg-bg-soft">
          <div className="flex h-10 shrink-0 items-center gap-2 border-b border-line px-3 text-sm font-medium text-ink">
            {mode === "rqt" ? <FileText size={16} aria-hidden className="text-accent-soft" /> : <Braces size={16} aria-hidden className="text-accent-soft" />}
            {mode === "rqt" ? "main.rqt" : "game.rqg"}
          </div>
          {mode === "rqt" ? (
            <textarea
              className="min-h-0 flex-1 overflow-auto bg-transparent p-3 font-mono text-sm leading-6 text-ink outline-none"
              spellCheck={false}
              wrap="off"
              value={source}
              onChange={(event) => updateSource(event.target.value, "small_text_edit")}
            />
          ) : (
            <pre className="min-h-0 flex-1 overflow-auto p-3 font-mono text-xs leading-5 text-ink">
              {json}
            </pre>
          )}
        </div>
        {mode === "rqt" ? <RuntimePreview result={result} /> : <JsonPreview json={json} />}
      </section>
      {result.errors.length > 0 && (
        <div className="mt-3 rounded-lg border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-300">
          {result.errors.map((error) => (
            <div key={`${error.line}:${error.code}`}>
              line {error.line}: {error.message}
            </div>
          ))}
        </div>
      )}
      <section className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <InlineLab />
        <GameTurnLab />
      </section>
    </GuideSection>
  );
}

function GuideSection({
  id,
  eyebrow,
  title,
  body,
  children,
  defaultOpen = true,
}: {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section id={id} className="scroll-mt-16">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3 border-b border-line pb-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-normal text-accent-soft">{eyebrow}</div>
          <h3 className="mt-1 text-lg font-semibold text-ink">{title}</h3>
          <p className="mt-1 max-w-4xl text-sm leading-6 text-ink-soft">{body}</p>
        </div>
        <button
          className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft hover:bg-bg-raised hover:text-ink"
          aria-expanded={open}
          onClick={() => setOpen((next) => !next)}
        >
          {open ? "접기" : "펼치기"}
        </button>
      </div>
      {open && children}
    </section>
  );
}

function _RqtGuide({ setSource }: { source: string; setSource: (value: string) => void }) {
  return (
    <div className="flex flex-col gap-4">
      <BeginnerGuide setSource={setSource} />
      <AssetBotFiveMinuteGuide setSource={setSource} />
      <CommonConfusionGuide />
      <InlineLab />
      <GameTurnLab />
      <RqtPackInstallLab />
      <SpecCoverageGuide title="문서 범위 지도" groups={RQT_SPEC_GROUPS} />

      <section className="rounded-lg border border-line bg-bg-soft p-3">
        <div className="mb-3 flex items-center gap-2">
          <Code2 size={16} aria-hidden className="text-accent-soft" />
          <h3 className="text-sm font-semibold text-ink">명령: 한글 문법과 English Expert Mode</h3>
        </div>
        <div className="grid gap-3 xl:grid-cols-2">
          {COMMAND_GUIDES.map((guide) => (
            <CommandCard key={guide.title} guide={guide} setSource={setSource} />
          ))}
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <ExpressionGuide />
        <LifecycleGuide />
      </section>
    </div>
  );
}

function BeginnerGuide({ setSource }: { setSource: (value: string) => void }) {
  return (
    <section className="rounded-lg border border-line bg-bg-soft p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BookOpen size={16} aria-hidden className="text-accent-soft" />
          <h3 className="text-sm font-semibold text-ink">처음 보는 사람을 위한 기본 가이드</h3>
        </div>
        <button
          className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft hover:bg-bg-raised hover:text-ink"
          onClick={() => setSource(ASSETBOT_STARTER_SAMPLE)}
        >
          초보 샘플 불러오기
        </button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {BASIC_GUIDE_CARDS.map((card) => (
          <article key={card.title} className="min-w-0 rounded-lg border border-line bg-bg-raised p-3">
            <h4 className="text-sm font-semibold text-ink">{card.title}</h4>
            <p className="mt-2 text-xs leading-5 text-ink-soft">{card.body}</p>
            <pre className="mt-3 overflow-x-auto whitespace-pre rounded border border-line bg-bg-soft p-2 font-mono text-[11px] leading-4 text-accent-soft">
              {card.example}
            </pre>
          </article>
        ))}
      </div>
    </section>
  );
}

function AssetBotFiveMinuteGuide({ setSource }: { setSource: (value: string) => void }) {
  return (
    <section className="rounded-lg border border-line bg-bg-soft p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={16} aria-hidden className="text-accent-soft" />
          <h3 className="text-sm font-semibold text-ink">5분만에 에셋봇 만들기</h3>
        </div>
        <button
          className="rounded-lg border border-accent bg-accent/15 px-3 py-1.5 text-xs font-medium text-ink hover:bg-accent/25"
          onClick={() => setSource(ASSETBOT_STARTER_SAMPLE)}
        >
          템플릿을 편집기에 넣기
        </button>
      </div>
      <div className="grid gap-3 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <div className="grid gap-2">
          {ASSETBOT_STEPS.map((step) => (
            <article key={step.label} className="grid grid-cols-[3.5rem_minmax(0,1fr)] items-center gap-3 rounded-lg border border-line bg-bg-raised p-3">
              <div className="flex min-h-14 items-center justify-center rounded-md border border-accent/40 bg-accent/15 px-2 py-1 text-center text-xs font-semibold text-accent-soft">
                {step.label}
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-semibold text-ink">{step.title}</h4>
                <p className="mt-1 text-xs leading-5 text-ink-soft">{step.body}</p>
              </div>
            </article>
          ))}
        </div>
        <CodeBlock title="복붙 가능한 에셋봇 시작 템플릿" value={ASSETBOT_STARTER_SAMPLE} />
      </div>
    </section>
  );
}

function RqtExamplesSection({ onLoadSource }: { onLoadSource: (value: string) => void }) {
  const [activeId, setActiveId] = useState(RQT_EXAMPLE_CARDS[0]?.id ?? "");
  const [message, setMessage] = useState<string | null>(null);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [importedExample, setImportedExample] = useState<{ cardId: string; title: string } | null>(null);
  const setView = useUiStore((state) => state.setView);
  const setActiveChatId = useUiStore((state) => state.setActiveChatId);
  const setSidebarOpen = useUiStore((state) => state.setSidebarOpen);
  const activeExample = RQT_EXAMPLE_CARDS.find((example) => example.id === activeId) ?? RQT_EXAMPLE_CARDS[0];
  const basicExamples = RQT_EXAMPLE_CARDS.filter((example) => example.group === "basic");
  const recipeExamples = RQT_EXAMPLE_CARDS.filter((example) => example.group === "recipe");

  const viewSource = (example: RqtExampleCard) => {
    setActiveId(example.id);
    setImportedExample(null);
    setMessage(`Showing source for ${example.title}.`);
  };

  const copySource = async (example: RqtExampleCard) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard API is not available.");
      await navigator.clipboard.writeText(example.source);
      setImportedExample(null);
      setMessage(`Copied RQT source for ${example.title}.`);
    } catch (error) {
      setImportedExample(null);
      setMessage(`Copy failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const openImportedGameList = () => {
    setActiveChatId(null);
    setSidebarOpen(false);
    setView("games");
  };

  const importExample = async (example: RqtExampleCard) => {
    setImportingId(example.id);
    setImportedExample(null);
    setMessage(null);
    try {
      const file = new File([example.source], example.filename, { type: "text/plain" });
      if (typeof file.text !== "function") {
        Object.defineProperty(file, "text", { value: async () => example.source });
      }
      const card = await importGameCard(file);
      setImportedExample({ cardId: card.id, title: card.definition.title });
      setMessage("예제를 가져왔습니다.");
    } catch (error) {
      setImportedExample(null);
      setMessage(`Import failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setImportingId(null);
    }
  };

  return (
    <GuideSection
      id="examples"
      eyebrow="Stable RQT"
      title="Validated examples"
      body="Browse the same Stable RQT source files that are covered by the core validation harness. These examples stay on the normal .rqt import, .rqg document, normalized GameDefinition, and runtime compile path."
    >
      <div className="grid gap-4">
        <div className="rounded-lg border border-line bg-bg-soft p-3 text-sm leading-6 text-ink-soft">
          {RQT_IMAGE_PLACEHOLDER_NOTE}
        </div>

        {message && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-bg-raised px-3 py-2 text-sm text-ink-soft">
            <div className="min-w-0">
              <div>{message}</div>
              {importedExample && (
                <div className="mt-1 break-words text-xs text-ink-faint">
                  가져온 게임: {importedExample.title}
                </div>
              )}
            </div>
            {importedExample && (
              <button
                className="inline-flex min-h-8 items-center gap-1.5 rounded border border-accent bg-accent/15 px-2.5 py-1 text-xs font-medium text-ink hover:bg-accent/25"
                onClick={openImportedGameList}
                data-card-id={importedExample.cardId}
              >
                <Gamepad2 size={13} aria-hidden />
                게임 목록에서 보기
              </button>
            )}
          </div>
        )}

        <ExampleGroup
          title="Basic examples"
          examples={basicExamples}
          activeId={activeExample?.id}
          importingId={importingId}
          onViewSource={viewSource}
          onCopySource={(example) => void copySource(example)}
          onImportExample={(example) => void importExample(example)}
        />

        <ExampleGroup
          title="Recipe examples"
          examples={recipeExamples}
          activeId={activeExample?.id}
          importingId={importingId}
          onViewSource={viewSource}
          onCopySource={(example) => void copySource(example)}
          onImportExample={(example) => void importExample(example)}
        />

        {activeExample && (
          <section className="rounded-lg border border-line bg-bg-soft p-3">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-normal text-accent-soft">
                  RQT source
                </div>
                <h4 className="mt-1 text-sm font-semibold text-ink">{activeExample.title}</h4>
                <p className="mt-1 text-xs text-ink-faint">{activeExample.sourcePath}</p>
              </div>
              <button
                className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 py-1.5 text-xs text-ink-soft hover:bg-bg-raised hover:text-ink"
                onClick={() => onLoadSource(activeExample.source)}
              >
                <Code2 size={14} aria-hidden />
                Load in syntax lab
              </button>
            </div>
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded border border-line bg-bg-raised p-3 font-mono text-xs leading-5 text-ink">
              {activeExample.source}
            </pre>
          </section>
        )}

        <section className="rounded-lg border border-line bg-bg-soft p-3">
          <div className="mb-3 flex items-center gap-2">
            <FileText size={16} aria-hidden className="text-accent-soft" />
            <h3 className="text-sm font-semibold text-ink">Roadmap / placeholders</h3>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {RQT_ROADMAP_CARDS.map((example) => (
              <article key={example.id} className="rounded-lg border border-line bg-bg-raised p-3">
                <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                  <h4 className="text-sm font-semibold text-ink">{example.title}</h4>
                  <span className="rounded border border-amber-700/70 bg-amber-950/40 px-2 py-0.5 text-[11px] font-medium text-amber-300">
                    planned recipe
                  </span>
                </div>
                <p className="text-xs leading-5 text-ink-soft">{example.description}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {example.tags.map((tag) => (
                    <span key={tag} className="rounded border border-line bg-bg-soft px-2 py-1 text-[11px] text-ink-faint">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>
    </GuideSection>
  );
}

function ExampleGroup({
  title,
  examples,
  activeId,
  importingId,
  onViewSource,
  onCopySource,
  onImportExample,
}: {
  title: string;
  examples: RqtExampleCard[];
  activeId?: string;
  importingId: string | null;
  onViewSource: (example: RqtExampleCard) => void;
  onCopySource: (example: RqtExampleCard) => void;
  onImportExample: (example: RqtExampleCard) => void;
}) {
  return (
    <section className="rounded-lg border border-line bg-bg-soft p-3">
      <div className="mb-3 flex items-center gap-2">
        <BookOpen size={16} aria-hidden className="text-accent-soft" />
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {examples.map((example) => (
          <article
            key={example.id}
            className={[
              "min-w-0 rounded-lg border bg-bg-raised p-3",
              activeId === example.id ? "border-accent/70" : "border-line",
            ].join(" ")}
          >
            <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
              <h4 className="text-sm font-semibold text-ink">{example.title}</h4>
              <span className="rounded border border-emerald-700/70 bg-emerald-950/40 px-2 py-0.5 text-[11px] font-medium text-emerald-300">
                {example.group === "basic" ? "Basic" : "Recipe"}
              </span>
            </div>
            <p className="text-xs leading-5 text-ink-soft">{example.description}</p>
            <p className="mt-2 text-[11px] leading-4 text-ink-faint">Teaches: {example.teaches}</p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {example.tags.map((tag) => (
                <span key={tag} className="rounded border border-line bg-bg-soft px-2 py-1 text-[11px] text-ink-faint">
                  {tag}
                </span>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                className="inline-flex min-h-8 items-center gap-1.5 rounded border border-line px-2.5 py-1 text-xs text-ink-soft hover:bg-bg-soft hover:text-ink"
                onClick={() => onViewSource(example)}
              >
                <Eye size={13} aria-hidden />
                View source
              </button>
              <button
                className="inline-flex min-h-8 items-center gap-1.5 rounded border border-line px-2.5 py-1 text-xs text-ink-soft hover:bg-bg-soft hover:text-ink"
                onClick={() => onCopySource(example)}
              >
                <Copy size={13} aria-hidden />
                Copy RQT
              </button>
              <button
                className="inline-flex min-h-8 items-center gap-1.5 rounded border border-accent bg-accent/15 px-2.5 py-1 text-xs font-medium text-ink hover:bg-accent/25 disabled:cursor-not-allowed disabled:border-line disabled:bg-bg-soft disabled:text-ink-faint"
                disabled={importingId === example.id}
                onClick={() => onImportExample(example)}
              >
                <Upload size={13} aria-hidden />
                {importingId === example.id ? "Importing..." : "Import as game"}
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function CommonConfusionGuide() {
  return (
    <section className="rounded-lg border border-line bg-bg-soft p-3">
      <div className="mb-3 flex items-center gap-2">
        <ShieldAlert size={16} aria-hidden className="text-accent-soft" />
        <h3 className="text-sm font-semibold text-ink">초보자가 자주 헷갈리는 구분</h3>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {COMMON_CONFUSIONS.map((item) => (
          <article key={item.title} className="rounded-lg border border-line bg-bg-raised p-3">
            <h4 className="text-sm font-semibold text-ink">{item.title}</h4>
            <div className="mt-2 grid gap-2 text-xs leading-5">
              <p className="rounded border border-amber-800/50 bg-amber-950/25 px-2 py-1.5 text-amber-200">
                {item.wrong}
              </p>
              <p className="rounded border border-emerald-800/50 bg-emerald-950/25 px-2 py-1.5 text-emerald-200">
                {item.right}
              </p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function GameTurnLab() {
  const [turn, setTurn] = useState(GAME_TURN_SAMPLE);
  const parsed = useMemo(() => parseTurn(turn), [turn]);
  const output = useMemo(() => JSON.stringify({
    narration: parsed.narration,
    choices: parsed.choices,
    stateDelta: parsed.stateDelta,
    scene: parsed.scene ?? null,
    rolls: parsed.rolls,
  }, null, 2), [parsed]);

  return (
    <section className="rounded-lg border border-line bg-bg-soft p-3">
      <div className="mb-3 flex items-center gap-2">
        <Gamepad2 size={16} aria-hidden className="text-accent-soft" />
        <h3 className="text-sm font-semibold text-ink">게임 턴 명령 플레이그라운드</h3>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <label className="grid min-w-0 gap-1 text-xs text-ink-faint">
          입력
          <textarea
            className="min-h-48 resize-y rounded border border-line bg-bg-raised p-3 font-mono text-xs leading-5 text-ink outline-none focus:border-accent"
            spellCheck={false}
            value={turn}
            onChange={(event) => setTurn(event.target.value)}
          />
        </label>
        <div className="min-w-0 rounded border border-line bg-bg-raised p-3">
          <div className="mb-2 text-xs font-semibold text-ink-faint">파싱 결과</div>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-5 text-ink">
            {output}
          </pre>
        </div>
      </div>
    </section>
  );
}

function RqtPackInstallLab() {
  const installed = useLive<InstalledRqtPack[]>(() => storage.liveInstalledRqtPacks(), []) ?? [];
  const [preview, setPreview] = useState<RqtPackAnalysis | null>(null);
  const [bytes, setBytes] = useState<Uint8Array | null>(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function analyzeFile(file: File | undefined): Promise<void> {
    if (!file) return;
    setBusy(true);
    setMessage("");
    setFileName(file.name);
    try {
      const data = new Uint8Array(await file.arrayBuffer());
      setBytes(data);
      setPreview(await analyzeRqtPack(data));
    } catch (error) {
      setBytes(null);
      setPreview(null);
      setMessage(error instanceof Error ? error.message : "패키지 분석에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  }

  async function installPreview(): Promise<void> {
    if (!preview?.installable || !preview.manifest || !bytes) return;
    setBusy(true);
    setMessage("");
    try {
      const now = Date.now();
      const pack: InstalledRqtPack = {
        id: newInstalledRqtPackId(),
        packageId: preview.manifest.id,
        name: preview.manifest.name,
        version: preview.manifest.version,
        type: preview.manifest.type,
        enabled: true,
        installedAt: now,
        sourceHash: await hashBytes(bytes),
        sourceFileName: fileName || undefined,
        archiveType: preview.archiveType,
        manifest: preview.manifest,
        entries: preview.entries,
        scripts: preview.scripts,
        assets: preview.assets,
        characters: preview.characters,
        approvedPermissions: preview.permissions,
        acceptedRecommendations: preview.recommendations,
        warnings: preview.warnings,
        sync: { rev: 0, createdAt: now, updatedAt: now, deletedAt: null },
      };
      await storage.installedRqtPacks.put(pack);
      setMessage(`${pack.name} ${pack.version} 패키지를 로컬 레지스트리에 등록했습니다.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "패키지 등록에 실패했습니다.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-lg border border-line bg-bg-soft p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Package size={16} aria-hidden className="text-accent-soft" />
          <h3 className="text-sm font-semibold text-ink">.rqp 설치 미리보기</h3>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised hover:text-ink">
          <Upload size={15} aria-hidden />
          파일 선택
          <input
            className="sr-only"
            type="file"
            accept=".rqp,.zip,.tar,application/zip,application/x-tar"
            onChange={(event) => void analyzeFile(event.currentTarget.files?.[0])}
          />
        </label>
      </div>

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.1fr)_minmax(18rem,0.9fr)]">
        <div className="min-w-0 rounded border border-line bg-bg-raised p-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-ink-faint">
            <ShieldAlert size={14} aria-hidden />
            분석 결과
          </div>
          {!preview ? (
            <p className="text-sm leading-6 text-ink-soft">
              ZIP 또는 TAR 기반 `.rqp`을 선택하면 루트 `manifest.json`, `entry.game`,
              경로 안전성, 권한, 추천 설정을 설치 전에 확인합니다.
            </p>
          ) : (
            <div className="grid gap-3">
              <div className="grid gap-2 sm:grid-cols-2">
                <SyntaxLine label="파일" value={fileName || "(이름 없음)"} />
                <SyntaxLine label="컨테이너" value={preview.archiveType} />
                <SyntaxLine label="패키지" value={preview.manifest ? `${preview.manifest.name} ${preview.manifest.version}` : "(manifest 없음)"} />
                <SyntaxLine label="entry.game" value={preview.manifest?.entry?.game ?? "(없음)"} />
              </div>
              <div className="flex flex-wrap gap-1.5">
                <PackPill label={`scripts ${preview.scripts.length}`} />
                <PackPill label={`assets ${preview.assets.length}`} />
                <PackPill label={`characters ${preview.characters.length}`} />
                <PackPill label={`files ${preview.entries.length}`} />
              </div>
              <PackIssueList title="오류" issues={preview.errors} tone="error" />
              <PackIssueList title="경고" issues={preview.warnings} tone="warning" />
            </div>
          )}
          {message && (
            <div className="mt-3 rounded border border-line bg-bg-soft px-2 py-1.5 text-xs text-ink-soft">
              {message}
            </div>
          )}
        </div>

        <div className="grid min-w-0 gap-3">
          <div className="rounded border border-line bg-bg-raised p-3">
            <div className="mb-2 text-xs font-semibold text-ink-faint">권한</div>
            {preview?.permissions.length ? (
              <div className="flex flex-wrap gap-1.5">
                {preview.permissions.map((permission) => (
                  <span
                    key={permission.name}
                    className={[
                      "rounded border px-2 py-1 text-[11px]",
                      permission.sensitive
                        ? "border-red-800/70 bg-red-950/40 text-red-300"
                        : "border-line bg-bg-soft text-ink-soft",
                    ].join(" ")}
                  >
                    {permission.name}: {permission.decision}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-sm text-ink-faint">(선언 없음)</span>
            )}
          </div>

          <div className="rounded border border-line bg-bg-raised p-3">
            <div className="mb-2 text-xs font-semibold text-ink-faint">추천 설정</div>
            {preview?.recommendations.length ? (
              <div className="grid gap-1.5">
                {preview.recommendations.map((item) => (
                  <SyntaxLine
                    key={`${item.source}:${item.key}`}
                    label={`${item.source}.${item.key}`}
                    value={formatPackValue(item.value)}
                  />
                ))}
              </div>
            ) : (
              <span className="text-sm text-ink-faint">(선언 없음)</span>
            )}
          </div>

          <div className="rounded border border-line bg-bg-raised p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-ink-faint">
              <Database size={14} aria-hidden />
              로컬 레지스트리
            </div>
            <p className="mb-2 text-xs leading-5 text-ink-soft">
              권한과 추천 설정을 확인한 뒤에만 설치 기록을 저장합니다. 추천 설정은 자동 적용하지 않고 승인 기록으로 남깁니다.
            </p>
            <button
              className="w-full rounded-lg border border-accent bg-accent/20 px-3 py-2 text-sm font-medium text-ink disabled:cursor-not-allowed disabled:border-line disabled:bg-bg-soft disabled:text-ink-faint"
              disabled={busy || !preview?.installable}
              onClick={() => void installPreview()}
            >
              {busy ? "처리 중..." : "승인하고 등록"}
            </button>
            <div className="mt-2 text-xs text-ink-faint">등록된 패키지 {installed.length}개</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PackPill({ label }: { label: string }) {
  return (
    <span className="rounded border border-line bg-bg-soft px-2 py-1 text-xs text-ink-soft">
      {label}
    </span>
  );
}

function PackIssueList({
  title,
  issues,
  tone,
}: {
  title: string;
  issues: { code: string; message: string; path?: string }[];
  tone: "error" | "warning";
}) {
  if (!issues.length) return null;
  const cls = tone === "error" ? "border-red-800 bg-red-950/40 text-red-300" : "border-amber-800 bg-amber-950/40 text-amber-300";
  return (
    <div className={`rounded border px-2 py-1.5 text-xs ${cls}`}>
      <div className="mb-1 font-semibold">{title}</div>
      <div className="grid gap-1">
        {issues.map((issue) => (
          <div key={`${issue.code}:${issue.path ?? issue.message}`}>
            {issue.code}: {issue.path ? `${issue.path} - ` : ""}
            {issue.message}
          </div>
        ))}
      </div>
    </div>
  );
}

function formatPackValue(value: unknown): string {
  if (typeof value === "string") return value;
  return JSON.stringify(value) ?? String(value);
}

function SpecCoverageGuide({ title, groups }: { title: string; groups: SpecGroup[] }) {
  return (
    <section className="rounded-lg border border-line bg-bg-soft p-3">
      <div className="mb-3 flex items-center gap-2">
        <Braces size={16} aria-hidden className="text-accent-soft" />
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {groups.map((group) => (
          <SpecCard key={group.title} group={group} />
        ))}
      </div>
    </section>
  );
}

function SpecCard({ group }: { group: SpecGroup }) {
  return (
    <article className="min-w-0 rounded-lg border border-line bg-bg-raised p-3">
      <div className="mb-2 flex items-start justify-between gap-2">
        <h4 className="min-w-0 text-sm font-semibold text-ink">{group.title}</h4>
        <StatusBadge status={group.status} />
      </div>
      <p className="mb-3 text-xs leading-5 text-ink-faint">{group.body}</p>
      <div className="flex flex-wrap gap-1.5">
        {group.examples.map((example) => (
          <code
            key={example}
            className="max-w-full overflow-x-auto rounded border border-line bg-bg-soft px-2 py-1 text-xs leading-5 text-accent-soft"
          >
            {example}
          </code>
        ))}
      </div>
    </article>
  );
}

function StatusBadge({ status }: { status: SpecStatus }) {
  const cls =
    status === "Stable"
      ? "border-emerald-700/70 bg-emerald-950/40 text-emerald-300"
      : status === "Preview"
        ? "border-sky-700/70 bg-sky-950/40 text-sky-300"
        : status === "Experimental"
          ? "border-amber-700/70 bg-amber-950/40 text-amber-300"
          : "border-red-800/70 bg-red-950/40 text-red-300";
  return (
    <span className={`shrink-0 rounded border px-2 py-0.5 text-[11px] font-medium ${cls}`}>
      {status}
    </span>
  );
}

function InlineLab() {
  const [charName, setCharName] = useState("하린");
  const [userName, setUserName] = useState("민준");
  const [text, setText] = useState("@캐릭터@은는 @유저@이가 마음에 든다. 호감도는 @호감도, 다음은 @(호감도 + 5). 이름표: @(이름 & \"님\") / @@username");
  const rendered = useMemo(
    () => renderRoqeetText(text, { char: charName, user: userName, values: { 호감도: 55, 이름: "로켓" } }),
    [charName, text, userName],
  );

  return (
    <section className="rounded-lg border border-line bg-bg-soft p-3">
      <div className="mb-3 flex items-center gap-2">
        <Wand2 size={16} aria-hidden className="text-accent-soft" />
        <h3 className="text-sm font-semibold text-ink">인라인 값과 조사 커넥터</h3>
      </div>
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.75fr)]">
        <div className="grid gap-2">
          <label className="grid gap-1 text-xs text-ink-faint">
            입력
            <input
              className="rounded border border-line bg-bg-raised px-3 py-2 text-sm text-ink outline-none focus:border-accent"
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="grid gap-1 text-xs text-ink-faint">
              캐릭터
              <input
                className="rounded border border-line bg-bg-raised px-3 py-2 text-sm text-ink outline-none focus:border-accent"
                value={charName}
                onChange={(event) => setCharName(event.target.value)}
              />
            </label>
            <label className="grid gap-1 text-xs text-ink-faint">
              유저
              <input
                className="rounded border border-line bg-bg-raised px-3 py-2 text-sm text-ink outline-none focus:border-accent"
                value={userName}
                onChange={(event) => setUserName(event.target.value)}
              />
            </label>
          </div>
        </div>
        <div className="rounded border border-line bg-bg-raised p-3">
          <div className="mb-2 text-xs font-semibold text-ink-faint">출력</div>
          <p className="break-words text-sm leading-6 text-ink">{rendered}</p>
          <div className="mt-3 flex flex-wrap gap-1.5 text-xs text-accent-soft">
            <code>@유저</code>
            <code>@캐릭터</code>
            <code>@은는</code>
            <code>@이가</code>
            <code>@을를</code>
            <code>@var</code>
            <code>@(식)</code>
            <code>&</code>
            <code>@@</code>
            <code>{"{{var}} 아님"}</code>
          </div>
        </div>
      </div>
    </section>
  );
}

function CommandCard({ guide, setSource }: { guide: GuideRow; setSource: (value: string) => void }) {
  const result = useMemo(() => runRoqeet(guide.sample, RUN_CONTEXT), [guide.sample]);

  return (
    <article className="min-w-0 rounded-lg border border-line bg-bg-raised p-3">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-ink">{guide.title}</h4>
          <p className="mt-1 text-xs leading-5 text-ink-faint">{guide.description}</p>
        </div>
        <button
          className="shrink-0 rounded border border-line px-2 py-1 text-xs text-ink-soft hover:bg-bg-soft"
          onClick={() => setSource(guide.sample)}
        >
          불러오기
        </button>
      </div>
      <div className="grid gap-2">
        <SyntaxLine label="한글" value={guide.ko} />
        <SyntaxLine label="English" value={guide.en} />
      </div>
      <div className="mt-3 grid gap-2 lg:grid-cols-2">
        <CodeBlock title="예시" value={guide.sample} />
        <OutputMini result={result} />
      </div>
    </article>
  );
}

function ExpressionGuide() {
  return (
    <section className="rounded-lg border border-line bg-bg-soft p-3">
      <h3 className="mb-2 text-sm font-semibold text-ink">연산식</h3>
      <p className="mb-3 text-xs leading-5 text-ink-faint">
        텍스트에서는 <code>@var</code>로 상태나 내장 값을 넣고, <code>@(식)</code>으로 계산 결과를 넣습니다.
        <code>@만약 식 {"{ ... }"}</code>, <code>@설정 값 = 식</code> 같은 expression-only 자리에서는 <code>@</code>를 붙이지 않습니다.
        <code>+</code>는 숫자 덧셈, <code>&</code>는 문자열 연결, <code>@@</code>는 리터럴 <code>@</code>입니다.
        <code>{"{{var}}"}</code>는 Stable RQT 문법이 아니며, RQT 식은 JS/Lua가 아니라 제한된 안전 DSL입니다.
      </p>
      <CodeBlock
        title="Stable interpolation"
        value={`@state hp number initial=10
@state name string initial="로켓"
체력: @hp
다음 체력: @(hp + 1)
이름표: @(name & "님")`}
      />
      <div className="grid gap-2 sm:grid-cols-2">
        {EXPRESSION_ROWS.map((row) => (
          <div key={row.label} className="rounded border border-line bg-bg-raised p-2">
            <SyntaxLine label={row.label} value={row.value} />
            <p className="mt-2 text-[11px] leading-4 text-ink-faint">{row.note}</p>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded border border-line bg-bg-raised p-3 text-xs leading-5 text-ink-soft">
        계산 순서는 괄호, 함수, 제곱, 부호/부정, 곱하기·나누기·나머지, 더하기·빼기, 비교, 그리고, 또는 순서입니다.
        알 수 없는 변수나 잘못된 타입의 연산은 미리보기 오류로 표시됩니다. 실제 게임 규칙에서도 사용할 상태 키는 먼저 @state로 선언해야 합니다.
      </div>
    </section>
  );
}

function LifecycleGuide() {
  const [active, setActive] = useState<LifecycleId>("before");
  const current = LIFECYCLE[active];

  return (
    <section className="rounded-lg border border-line bg-bg-soft p-3">
      <h3 className="mb-2 text-sm font-semibold text-ink">응답 훅이 켜지는 타이밍</h3>
      <div className="grid gap-2 sm:grid-cols-5">
        {(Object.keys(LIFECYCLE) as LifecycleId[]).map((id) => (
          <button
            key={id}
            className={[
              "rounded border px-2 py-2 text-xs transition-colors",
              active === id
                ? "border-accent bg-accent/20 text-ink"
                : "border-line bg-bg-raised text-ink-soft hover:text-ink",
            ].join(" ")}
            onFocus={() => setActive(id)}
            onMouseEnter={() => setActive(id)}
            onClick={() => setActive(id)}
          >
            {LIFECYCLE[id].label}
          </button>
        ))}
      </div>
      <div className="mt-3 rounded border border-line bg-bg-raised p-3">
        <div className="text-sm font-semibold text-ink">{current.title}</div>
        <p className="mt-1 text-xs leading-5 text-ink-soft">{current.body}</p>
      </div>
      <div className="mt-3 grid gap-2 text-xs text-ink-soft">
        <SyntaxLine label="예정" value={'@응답전 { @기억읽기 "최근_약속" -> 최근약속 }'} />
        <SyntaxLine label="예정" value={'@응답후 { @기억저장 "마지막응답" = 응답.본문 }'} />
      </div>
    </section>
  );
}

function JsonGuide() {
  return (
    <div className="flex flex-col gap-4">
      <SpecCoverageGuide title=".rqg / 패키지 지도" groups={JSON_SPEC_GROUPS} />
      <section className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-lg border border-line bg-bg-soft p-3">
          <h3 className="mb-2 text-sm font-semibold text-ink">.rqg 읽는 법</h3>
          <div className="grid gap-2">
            <SyntaxLine label="kind" value="roqeet.game" />
            <SyntaxLine label="rqtVersion" value="0.1" />
            <SyntaxLine label="formatVersion" value="0.1" />
            <SyntaxLine label="nodes" value="game.meta, game.initialState, game.aiContract, game.rules, game.assets" />
            <SyntaxLine label="문서 성격" value="내부 실행 포맷이며 보안 경계가 아님" />
          </div>
        </div>
        <div className="rounded-lg border border-line bg-bg-soft p-3">
          <h3 className="mb-2 text-sm font-semibold text-ink">언제 쓰나</h3>
          <p className="text-sm leading-6 text-ink-soft">
            `.rqt`는 사람이 쓰는 문서이고, `.rqg`은 에디터 저장, 검증, 가져오기, 엔진 실행에
            가까운 구조화 문서입니다. `.rqp`은 실행 문법이 아니라 여러 파일과 에셋을 묶어 공유하는 배포 포맷입니다.
          </p>
        </div>
      </section>
    </div>
  );
}

function SegmentButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      className={[
        "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm",
        active
          ? "border-accent bg-accent/20 text-ink"
          : "border-line text-ink-soft hover:bg-bg-raised hover:text-ink",
      ].join(" ")}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function SyntaxLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded border border-line bg-bg-soft px-2 py-1.5">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-normal text-ink-faint">{label}</div>
      <code className="block overflow-x-auto whitespace-pre text-xs leading-5 text-accent-soft">{value}</code>
    </div>
  );
}

function CodeBlock({ title, value }: { title: string; value: string }) {
  return (
    <div className="min-w-0 rounded border border-line bg-bg-soft p-2">
      <div className="mb-1 text-[11px] font-semibold text-ink-faint">{title}</div>
      <pre className="overflow-x-auto whitespace-pre font-mono text-xs leading-5 text-ink">{value}</pre>
    </div>
  );
}

function OutputMini({ result }: { result: ReturnType<typeof runRoqeet> }) {
  const prompt = result.prompts.map((item) => `[${item.name}] ${item.content}`).join("\n");
  const ui = result.ui.map((item) => `버튼: ${item.label}`).join("\n");
  const media = result.media.map((item) => `이미지: ${item.asset}`).join("\n");
  const dialogues = result.dialogues.map((item) => `대사(${item.speaker}): ${item.content}`).join("\n");
  const scenes = result.scenes.map((item) => `장면: ${item.id}`).join("\n");
  const value = [result.text, prompt, dialogues, ui, media, scenes].filter(Boolean).join("\n");

  return (
    <div className="min-w-0 rounded border border-line bg-bg-soft p-2">
      <div className="mb-1 text-[11px] font-semibold text-ink-faint">결과</div>
      <pre className="max-h-44 overflow-auto whitespace-pre-wrap break-words font-sans text-xs leading-5 text-ink">
        {value || "(출력 없음)"}
      </pre>
    </div>
  );
}

function OutputBlock({
  icon,
  title,
  children,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 rounded-lg border border-line bg-bg-soft p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
        <span className="text-accent-soft">{icon}</span>
        {title}
      </div>
      {children}
    </div>
  );
}
